// @ts-nocheck
/**
 * Course Materials Sidebar Module
 * Handles all sidebar functionality including campus/term selection, course dropdowns, and student ID lookup
 */

/** Persists typed student ID across navigation when session/start does not issue a token (never use ?student_id=). */
const CMRP_STUDENT_ID_PREFILL_KEY = 'cmrp_student_id_prefill';
const CMRP_FLOW_STORAGE_KEY = 'cmrp_flow';
const CMRP_FLOW_QUERY_PARAM = 'cmrp_flow';
/** Set before navigating to CMRP from React / header / `?cmrp_flow=`; consumed when optional form scroll runs. */
const CMRP_SCROLL_FORM_ENTRY_ONCE = 'cmrp_scroll_form_entry_once';
/** Which CMRP entry path the user chose before navigation (mirrors main branch URL restore guard). */
const CMRP_INITIAL_FLOW_KEY = 'cmrp_initial_flow';

function setCmrpInitialFlowPreference(flow) {
  if (flow !== 'student-id' && flow !== 'course-builder') return;
  try {
    sessionStorage.setItem(CMRP_INITIAL_FLOW_KEY, flow);
  } catch (e) {
    /* ignore */
  }
}

function readCmrpInitialFlowPreference() {
  try {
    const raw = sessionStorage.getItem(CMRP_INITIAL_FLOW_KEY);
    if (raw === 'student-id' || raw === 'course-builder') return raw;
    return '';
  } catch (e) {
    return '';
  }
}

function cmrpMarkPendingFormEntryScroll() {
  try {
    sessionStorage.setItem(CMRP_SCROLL_FORM_ENTRY_ONCE, '1');
  } catch (e) {
    /* ignore */
  }
}

function cmrpConsumePendingFormEntryScroll() {
  try {
    const ok = sessionStorage.getItem(CMRP_SCROLL_FORM_ENTRY_ONCE) === '1';
    if (ok) sessionStorage.removeItem(CMRP_SCROLL_FORM_ENTRY_ONCE);
    return ok;
  } catch (e) {
    return false;
  }
}

window.cmrpMarkPendingFormEntryScroll = cmrpMarkPendingFormEntryScroll;
window.CMRP_INITIAL_FLOW_KEY = CMRP_INITIAL_FLOW_KEY;
window.setCmrpInitialFlowPreference = setCmrpInitialFlowPreference;
window.readCmrpInitialFlowPreference = readCmrpInitialFlowPreference;
window.cmrpPersistFlow = cmrpPersistFlow;

function cmrpNormalizeFlow(rawFlow) {
  const flow = String(rawFlow || '').trim().toLowerCase();
  if (flow === 'student-id' || flow === 'course-builder' || flow === 'sso') return flow;
  return '';
}

function cmrpPersistFlow(flow) {
  const normalized = cmrpNormalizeFlow(flow);
  try {
    if (normalized) {
      sessionStorage.setItem(CMRP_FLOW_STORAGE_KEY, normalized);
    } else {
      sessionStorage.removeItem(CMRP_FLOW_STORAGE_KEY);
    }
  } catch (e) {
    /* ignore storage failures */
  }
  return normalized;
}

function cmrpReadFlowFromStorage() {
  try {
    return cmrpNormalizeFlow(sessionStorage.getItem(CMRP_FLOW_STORAGE_KEY));
  } catch (e) {
    return '';
  }
}

function cmrpCurrentFlow() {
  const fromStorage = cmrpReadFlowFromStorage();
  if (fromStorage) return fromStorage;
  const fromQuery = cmrpNormalizeFlow(
    new URLSearchParams(window.location.search).get(CMRP_FLOW_QUERY_PARAM),
  );
  if (fromQuery) cmrpPersistFlow(fromQuery);
  return fromQuery;
}

function cmrpHydrateFlowFromQueryAndCleanUrl() {
  const params = new URLSearchParams(window.location.search);
  if (!params.has(CMRP_FLOW_QUERY_PARAM)) return;
  cmrpMarkPendingFormEntryScroll();
  const fromQuery = cmrpNormalizeFlow(params.get(CMRP_FLOW_QUERY_PARAM));
  cmrpPersistFlow(fromQuery);
  params.delete(CMRP_FLOW_QUERY_PARAM);
  const qs = params.toString();
  window.history.replaceState(
    {},
    '',
    qs ? `${window.location.pathname}?${qs}` : window.location.pathname,
  );
}

/**
 * CMRP: course-builder and student-id forms use separate element ids when both render (`cb` / `sid`).
 */
function cmrpPanel() {
  return document.getElementById('filters-section');
}

/** CMRP filter `<select>`: max chars in closed control (ellipsis after). */
const CMRP_SELECT_LABEL_MAX_LEN_DESKTOP = 10;
const CMRP_SELECT_LABEL_MAX_LEN_MOBILE = 20;

function cmrpSelectLabelMaxLen() {
  if (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 968px)').matches) {
    return CMRP_SELECT_LABEL_MAX_LEN_MOBILE;
  }
  return CMRP_SELECT_LABEL_MAX_LEN_DESKTOP;
}

/**
 * Full label for an `<option>` (stable via `data-cmrp-full-label`).
 * @param {HTMLOptionElement | null | undefined} opt
 * @returns {string}
 */
function cmrpOptionFullLabel(opt) {
  if (!opt) return '';
  const stored = opt.dataset?.cmrpFullLabel;
  if (stored != null && stored !== '') return stored;
  const t = String(opt.textContent ?? opt.text ?? '').trim();
  opt.dataset.cmrpFullLabel = t;
  return t;
}

/**
 * @param {HTMLOptionElement} option
 * @param {string} fullText
 */
function cmrpStampOptionFullLabel(option, fullText) {
  const s = String(fullText ?? '');
  option.dataset.cmrpFullLabel = s;
  option.textContent = s;
}

/**
 * @param {HTMLSelectElement | null | undefined} select
 * @returns {string}
 */
function cmrpSelectSelectedFullLabel(select) {
  if (!select || select.selectedIndex < 0) return '';
  const opt = select.options[select.selectedIndex];
  return opt ? cmrpOptionFullLabel(opt) : '';
}

/** Restore full option labels (never shorten list items) and set closed-field tooltip only. */
function cmrpTruncateCmrpFilterSelectSelected(select) {
  if (!select?.options) return;
  if (document.activeElement !== select) {
    for (let i = 0; i < select.options.length; i++) {
      const opt = select.options[i];
      const full = cmrpOptionFullLabel(opt);
      if (String(opt.textContent ?? '') !== full) {
        opt.textContent = full;
      }
    }
  }
  if (select.selectedIndex < 0) {
    select.title = '';
    return;
  }
  const selectedFull = cmrpSelectSelectedFullLabel(select);
  const maxLen = cmrpSelectLabelMaxLen();
  select.title = selectedFull.length > maxLen ? selectedFull : '';
}

function cmrpTruncateCmrpFilterSelectsIn(panel) {
  const root = panel || document;
  root
    .querySelectorAll('select.cmrp-cb-select, select.cmrp-student-glass-select')
    .forEach(sel => {
      cmrpTruncateCmrpFilterSelectSelected(sel);
    });
}

function cmrpAttachCmrpSelectTruncationListeners() {
  const panel = document.getElementById('filters-section');
  if (!panel || panel.dataset.cmrpSelectTruncationBound === '1') return;
  panel.dataset.cmrpSelectTruncationBound = '1';
  const selectMatcher = 'select.cmrp-cb-select, select.cmrp-student-glass-select';
  panel.addEventListener('change', e => {
    const t = e.target;
    if (t?.matches?.(selectMatcher)) {
      cmrpTruncateCmrpFilterSelectSelected(t);
    }
  });
}

/** Re-apply titles after async campus/term/DDCS fills `<option>` nodes (batched). */
function cmrpInstallCmrpSelectTruncationMutationObserver() {
  const panel = document.getElementById('filters-section');
  if (!panel || panel.dataset.cmrpSelectTruncationMo === '1') return;
  panel.dataset.cmrpSelectTruncationMo = '1';
  let moTimer;
  const scheduleUpdate = () => {
    clearTimeout(moTimer);
    moTimer = setTimeout(() => {
      window.cmrpTruncateCmrpFilterSelectsIn?.(panel);
    }, 100);
  };
  const mo = new MutationObserver(scheduleUpdate);
  mo.observe(panel, { subtree: true, childList: true });
}

window.cmrpOptionFullLabel = cmrpOptionFullLabel;
window.cmrpStampOptionFullLabel = cmrpStampOptionFullLabel;
window.cmrpTruncateCmrpFilterSelectsIn = cmrpTruncateCmrpFilterSelectsIn;
window.cmrpSelectSelectedFullLabel = cmrpSelectSelectedFullLabel;

function cmrpStudentIdThemeEnabled() {
  return cmrpPanel()?.dataset?.cmrpStudentIdActive === 'true';
}

/** Both CMRP forms visible: no mirroring; campus/term handlers target the row that changed. */
/** True when both CMRP forms are rendered (student ID theme on). */
function cmrpShowBothForms() {
  return cmrpPanel()?.dataset?.cmrpStudentIdActive === 'true';
}

/** When course-builder + student-id both render, sid row must not mutate cb globals or cb DOM. */
function cmrpIsolateStudentIdCampusTermFromCourseBuilder(rowScope) {
  return cmrpShowBothForms() && rowScope === 'sid';
}

/** Division → section + Add button (hidden independently of campus/term when course line is toggled off). */
function cmrpCourseBuilderCourseLineFieldEls() {
  return document.querySelectorAll(
    '.cmrp-filter-form--course-builder .cmrp-course-line-fields',
  );
}

function cmrpIsDesktopCmrpLayout() {
  return window.matchMedia && window.matchMedia('(min-width: 969px)').matches;
}

/** True on CMRP results template (`CourseMaterialsPage` adds `course-materials-page` to body). */
function cmrpIsCourseMaterialsResultsPage() {
  return document.body?.classList?.contains('course-materials-page') === true;
}

/** Remember original `#products-section` position in `.course-materials` for mobile / collapsed sidebar. */
let cmrpProductsDefaultSlot = null;

function rememberCmrpProductsDefaultSlot() {
  const products = document.getElementById('products-section');
  if (!products || cmrpProductsDefaultSlot) return;
  cmrpProductsDefaultSlot = { parent: products.parentNode, next: products.nextSibling };
}

function restoreCmrpProductsToDefaultSlot() {
  const products = document.getElementById('products-section');
  if (!products || !cmrpProductsDefaultSlot?.parent) return;
  const { parent, next } = cmrpProductsDefaultSlot;
  if (next && next.parentNode === parent) {
    parent.insertBefore(products, next);
  } else {
    parent.appendChild(products);
  }
}

/**
 * Dual CMRP only: true when docked `#products-section` targets `.cmrp-results-anchor--student-id`.
 * Same branching as `cmrpResolveProductsAnchorEl` (viewport check excluded). Used by URL restore
 * so only the anchored form reads deep-link params and course-builder chips stay empty.
 * @returns {boolean}
 */
function cmrpResolveProductsAnchorUnderStudentForm() {
  if (!cmrpShowBothForms()) {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  const flow = cmrpCurrentFlow();
  const panel = document.getElementById('filters-section');
  const sidMode = panel?.classList.contains('student-id-mode') === true;

  const hasCourseUrlParams =
    Boolean(params.get('departmentDisplayName')) ||
    Boolean(params.get('courseDisplayName')) ||
    Boolean(params.get('sectionDisplayName')) ||
    Boolean(params.get('courseRefId'));

  let shopByParam = '';
  try {
    const raw = params.get('shopBy');
    shopByParam = raw ? decodeURIComponent(raw) : '';
  } catch {
    shopByParam = params.get('shopBy') || '';
  }

  const shopByMeansCourseBuilder =
    shopByParam === 'course' || shopByParam === 'courseRefId';

  const page = window.courseMaterialsPageInstance;
  const filters = page?.selectedFilters;
  if (Array.isArray(filters) && filters.length > 0) {
    const last = filters[filters.length - 1];
    if (last?.fromStudentId === true) {
      return true;
    }
    if (flow === 'course-builder' || shopByMeansCourseBuilder) {
      return false;
    }
    if (flow === 'student-id' || flow === 'sso' || shopByParam === 'studentid') {
      return true;
    }
    if (cmrpIsCourseMaterialsResultsPage()) {
      return true;
    }
    return false;
  }

  // URL beats session `cmrp_flow`: a prior student-id visit leaves `student-id` in
  // sessionStorage while discover redirects with `shopBy=course`, `shopBy=courseRefId`, and/or `courseRefId`.
  if (shopByMeansCourseBuilder || Boolean(params.get('courseRefId'))) {
    return false;
  }

  if (flow === 'course-builder') {
    return false;
  }
  if (flow === 'student-id' || flow === 'sso') {
    return true;
  }

  if (hasCourseUrlParams) {
    return false;
  }

  if (cmrpIsCourseMaterialsResultsPage()) {
    return true;
  }

  if (sidMode) {
    return true;
  }

  const emptyBuilder =
    !params.get('sectionDisplayName') &&
    !params.get('courseDisplayName') &&
    !params.get('departmentDisplayName') &&
    !params.get('courseRefId');
  if (emptyBuilder) {
    return true;
  }
  return false;
}

/**
 * Mount `#products-section` under the course-builder or student-id `.cmrp-results-anchor`
 * (CMRP results: all viewports; other templates: desktop only — see anchorCmrpProductsPanel).
 */
function cmrpResolveProductsAnchorEl() {
  if (!cmrpIsDesktopCmrpLayout() && !cmrpIsCourseMaterialsResultsPage()) return null;

  if (!cmrpShowBothForms()) {
    return document.querySelector('.cmrp-results-anchor--course-builder');
  }

  return document.querySelector(
    cmrpResolveProductsAnchorUnderStudentForm()
      ? '.cmrp-results-anchor--student-id'
      : '.cmrp-results-anchor--course-builder',
  );
}

function anchorCmrpProductsPanel() {
  const products = document.getElementById('products-section');
  if (!products) return;
  rememberCmrpProductsDefaultSlot();

  if (!cmrpIsDesktopCmrpLayout() && !cmrpIsCourseMaterialsResultsPage()) {
    restoreCmrpProductsToDefaultSlot();
    return;
  }

  const target = cmrpResolveProductsAnchorEl();
  if (target) {
    target.appendChild(products);
  } else {
    restoreCmrpProductsToDefaultSlot();
  }

  if (typeof window.syncCmrpProductsSectionShell === 'function') {
    window.syncCmrpProductsSectionShell();
  }
}

window.anchorCmrpProductsPanel = anchorCmrpProductsPanel;
window.cmrpResolveProductsAnchorUnderStudentForm =
  cmrpResolveProductsAnchorUnderStudentForm;

/**
 * DDCS / URL-hydration trace. Enable either:
 * - `window.__cmrpDdcsDebug = true` then refresh, or
 * - add `cmrp_ddcs_debug=1` to the page query string.
 * Filter console by: [CMRP DDCS]
 */
function cmrpDdcsDebugEnabled() {
  try {
    if (typeof window === 'undefined') return false;
    if (window.__cmrpDdcsDebug === true) return true;
    return new URLSearchParams(window.location.search).get('cmrp_ddcs_debug') === '1';
  } catch (e) {
    return false;
  }
}

function cmrpDdcsDebug(tag, detail = {}) {
  if (!cmrpDdcsDebugEnabled()) return;
  console.info('[CMRP DDCS]', tag, detail);
}

if (typeof window !== 'undefined') {
  window.cmrpDdcsDebug = cmrpDdcsDebug;
  window.cmrpCurrentFlow = cmrpCurrentFlow;
  window.cmrpPersistFlow = cmrpPersistFlow;
}

/** `cb` vs `sid` campus/term row, from a campus or term `<select>` id. */
function cmrpScopeFromCtSelect(el) {
  if (!el?.id) return 'cb';
  if (el.id.endsWith('-sid')) return 'sid';
  return 'cb';
}

/** Course-builder desktop campus/term `<select>` (cb suffix when dual-form). */
function cmrpCourseBuilderCampusTermEl(inner) {
  const cb = cmrpSuffixPair().cb;
  if (cb) return document.getElementById(cmrpId(inner, 'cb'));
  return typeof window.cmrpQueryDesktopCampusTerm === 'function'
    ? window.cmrpQueryDesktopCampusTerm(inner)
    : null;
}

function cmrpSuffixPair() {
  const p = cmrpPanel();
  if (!cmrpStudentIdThemeEnabled()) return { cb: '', sid: '' };
  return {
    cb: (p?.dataset?.cmrpSuffixCourseBuilder || 'cb').trim(),
    sid: (p?.dataset?.cmrpSuffixStudentId || 'sid').trim(),
  };
}

function cmrpActiveCampusTermSuffix() {
  const { cb, sid } = cmrpSuffixPair();
  if (!cb && !sid) return '';
  const panel = cmrpPanel();
  const params = new URLSearchParams(window.location.search);
  const flow = cmrpCurrentFlow();
  if (
    flow === 'student-id' ||
    flow === 'sso' ||
    panel?.classList.contains('student-id-mode')
  ) {
    return sid || cb;
  }
  if (flow === 'course-builder') {
    return cb || sid;
  }
  if (
    cmrpStudentIdThemeEnabled() &&
    !flow &&
    !params.get('sectionDisplayName') &&
    !params.get('courseDisplayName') &&
    !params.get('departmentDisplayName')
  ) {
    return sid || cb;
  }
  return cb || sid;
}

/**
 * @param {'cb' | 'sid' | 'activeCt'} scope activeCt = campus/term row for the active flow
 */
function cmrpId(base, scope) {
  const { cb, sid } = cmrpSuffixPair();
  let suf = '';
  if (scope === 'cb') suf = cb;
  else if (scope === 'sid') suf = sid;
  else if (scope === 'activeCt') suf = cmrpActiveCampusTermSuffix();
  return suf ? `${base}-${suf}` : base;
}

function cmrpCampusTermRootId(scope = 'activeCt') {
  const suf =
    scope === 'activeCt'
      ? cmrpActiveCampusTermSuffix()
      : scope === 'cb'
        ? cmrpSuffixPair().cb
        : cmrpSuffixPair().sid;
  return suf ? `campus-term-dropdowns-${suf}` : 'campus-term-dropdowns';
}

/**
 * Student ID: `#campus-term-dropdowns-{sid}` is on `.cmrp-student-inline-row__inner` (flat layout).
 * Toggle visibility on campus/term `.form-group` children only — not the whole row.
 * Course builder keeps a dedicated wrapper; that element still uses display flex/none on the root.
 * @param {HTMLElement | null} rootEl
 * @param {'show' | 'hide'} mode
 */
function cmrpSetCampusTermDropdownContainerVisibility(rootEl, mode) {
  if (!rootEl) return;
  if (rootEl.classList?.contains('cmrp-student-inline-row__inner')) {
    const formGroups = rootEl.querySelectorAll(':scope > .form-group');
    const singleCampusUi = rootEl.dataset.cmrpCampusUi === 'single';
    formGroups.forEach((el, idx) => {
        if (mode === 'show') {
        if (singleCampusUi && idx === 0) {
          // Let CSS ([data-cmrp-campus-ui="single"]) collapse with transition; avoid display:none (not animatable).
          el.style.removeProperty('display');
          el.style.removeProperty('visibility');
          return;
        }
        el.style.display = '';
        el.style.visibility = 'visible';
      } else {
        el.style.display = 'none';
      }
    });
  } else if (mode === 'show') {
    rootEl.style.display = 'flex';
    rootEl.style.visibility = 'visible';
  } else {
    rootEl.style.display = 'none';
  }
}

function cmrpMirrorCampusTermBetweenForms() {
  if (cmrpShowBothForms()) return;
  const { cb, sid } = cmrpSuffixPair();
  if (!cb || !sid) return;
  const a = cmrpActiveCampusTermSuffix();
  const b = a === cb ? sid : cb;
  const pairs = [
    ['campus', 'campus'],
    ['term', 'term'],
  ];
  pairs.forEach(([key]) => {
    const src = document.querySelector(`#campus-term-dropdowns-${a} #${key}-${a}`);
    const dst = document.querySelector(`#campus-term-dropdowns-${b} #${key}-${b}`);
    if (src && dst && src !== dst) {
      dst.innerHTML = src.innerHTML;
      dst.disabled = src.disabled;
      dst.value = src.value;
    }
  });
}

window.cmrpId = cmrpId;
/** Course-builder campus/term `<select>` (cb row when dual-form; canonical when single-form). */
window.cmrpCourseBuilderCampusTermEl = cmrpCourseBuilderCampusTermEl;
window.cmrpQueryDesktopCampusTerm = function cmrpQueryDesktopCampusTerm(inner) {
  const root = cmrpCampusTermRootId('activeCt');
  const id = cmrpId(inner, 'activeCt');
  return document.querySelector(`#${root} #${id}`);
};

/**
 * Defer `#products-section` anchoring until after layout paint (no viewport scroll).
 */
function scheduleCmrpAnchorProductsPanel() {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => anchorCmrpProductsPanel());
  });
}

function cmrpStudentIdLabel() {
  if (typeof window.getCmrpStudentIdLabel === 'function') {
    return window.getCmrpStudentIdLabel();
  }
  const s = window.shopifySettings?.cmrpFlowSwitcherStudentPlaceholder;
  const t = s != null ? String(s).trim() : '';
  return t || 'Student ID';
}

/**
 * Shopify metaobject / JSON values may include HTML entities (e.g. &amp;). Decode for native selects and labels.
 */
function decodeHtmlEntities(text) {
  if (text == null || text === '') return text ?? '';
  const str = String(text);
  if (!str.includes('&')) return str;
  const ta = document.createElement('textarea');
  ta.innerHTML = str;
  return ta.value;
}

if (typeof window !== 'undefined') {
  window.decodeCmrpHtmlEntities = decodeHtmlEntities;
}

/** Lowercase, strip spaces, after decoding entities — for matching API names to UI values. */
function normalizeDdcsLabel(value) {
  return String(decodeHtmlEntities(value ?? ''))
    .toLowerCase()
    .replace(/\s+/g, '');
}

function cmrpHasOAuthCodeInUrl() {
  try {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('code')) return true;
    const h = window.location.hash || '';
    if (!h || h === '#') return false;
    const hp = new URLSearchParams(h.charAt(0) === '#' ? h.slice(1) : h);
    return Boolean(hp.get('code'));
  } catch (e) {
    return false;
  }
}

function cmrpFiltersPanelSsoActive(filtersPanel) {
  if (!filtersPanel?.dataset) return false;
  const ds = filtersPanel.dataset;
  const hasAuth =
    String(ds.ssoAuthUrl || '').trim() !== '' &&
    String(ds.ssoClientId || '').trim() !== '';
  if (!hasAuth) return false;
  if (ds.cmrpTypedStudentIdAllowed === 'false') return true;
  const enabled = String(ds.ssoEnabled || '').toLowerCase();
  return enabled === 'true' || enabled === '1';
}

if (typeof window !== 'undefined') {
  window.cmrpFiltersPanelSsoActive = cmrpFiltersPanelSsoActive;
}

/** Hide typed student ID in the student-id glass row when CMRP SSO is configured. */
function syncCmrpTypedStudentIdDomFlags() {
  const filters = document.getElementById('filters-section');
  const studentFlowOn = cmrpStudentIdThemeEnabled();
  const typedAllowed =
    studentFlowOn && filters && !cmrpFiltersPanelSsoActive(filters);

  if (!filters) return;
  if (!studentFlowOn) {
    delete filters.dataset.cmrpTypedStudentIdAllowed;
    filters.classList.remove('cmrp-sso-no-typed-student-id');
    return;
  }
  filters.dataset.cmrpTypedStudentIdAllowed = typedAllowed ? 'true' : 'false';
  if (typedAllowed) {
    filters.classList.remove('cmrp-sso-no-typed-student-id');
  } else {
    filters.classList.add('cmrp-sso-no-typed-student-id');
  }

  const typedSection = document.querySelector(
    '.cmrp-filter-form--student-id .student-id-section',
  );
  if (typedSection) {
    applyCmrpTypedStudentIdSectionVisibility(typedSection, {
      dualForms: cmrpShowBothForms(),
    });
  }
}

/** Inline `display` on the typed-ID row wins over CSS — keep it off when CMRP SSO is on. */
function applyCmrpTypedStudentIdSectionVisibility(section, { dualForms = false } = {}) {
  if (!section) return;
  const filters = document.getElementById('filters-section');
  if (cmrpFiltersPanelSsoActive(filters)) {
    section.hidden = true;
    section.style.setProperty('display', 'none', 'important');
    return;
  }
  section.hidden = false;
  section.style.removeProperty('display');
  if (!dualForms) {
    section.style.display = 'none';
  }
}

function cmrpReadInitialFlowPreferenceSafe() {
  if (typeof window.readCmrpInitialFlowPreference === 'function') {
    const v = window.readCmrpInitialFlowPreference();
    if (v === 'student-id' || v === 'course-builder') return v;
  }
  try {
    const raw = sessionStorage.getItem(CMRP_INITIAL_FLOW_KEY);
    if (raw === 'student-id' || raw === 'course-builder') return raw;
  } catch (e) {
    /* ignore */
  }
  return '';
}

/**
 * Student-id entry + SSO configured + no session + not mid-OAuth.
 * Revamped CMRP: applies to the student-id glass form (course-builder stays available).
 */
function cmrpShouldShowSsoFirstLanding() {
  if (!cmrpStudentIdThemeEnabled()) return false;
  if (cmrpHasOAuthCodeInUrl()) return false;

  const pref = cmrpReadInitialFlowPreferenceSafe();
  const flow = cmrpCurrentFlow();
  const onStudentPath =
    pref === 'student-id' ||
    flow === 'student-id' ||
    flow === 'sso' ||
    document.getElementById('filters-section')?.classList.contains('student-id-mode') ===
      true;

  if (!onStudentPath) return false;

  const filtersPanel = document.getElementById('filters-section');
  if (!cmrpFiltersPanelSsoActive(filtersPanel)) return false;

  try {
    if (sessionStorage.getItem('sessionToken')) return false;
    if (sessionStorage.getItem('cmrp_sso_prefill')) return false;
  } catch (e) {
    return false;
  }
  return true;
}

const CMRP_SSO_LANDING_HINT_HTML =
  '<p class="cmrp-sso-landing-panel__hint">Use your school account to load your courses.</p>';

function cmrpGetSsoLandingBtnFragmentHtml(fragment) {
  const tpl = document.getElementById('cmrp-sso-landing-btn-fragments');
  if (!tpl?.content) return '';
  const node = tpl.content.querySelector(`[data-cmrp-sso-fragment="${fragment}"]`);
  return node ? node.outerHTML : '';
}

function cmrpBuildSsoLandingGlassHtml(filtersDataset) {
  const ds = filtersDataset || {};
  const esc = s =>
    String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');

  const btnLabel =
    String(ds.ssoCmrpButtonText || '').trim() ||
    (typeof window.shopifySettings?.ssoCmrpButtonText === 'string'
      ? window.shopifySettings.ssoCmrpButtonText.trim()
      : '') ||
    'STUDENT LOGIN';

  return `
      <div class="cmrp-sso-landing-panel__inner">
        <button
          type="button"
          class="btn-primary cmrp-student-shop-btn cmrp-sso-landing-panel__btn"
          data-sso-button="true"
          data-auth-url="${esc(ds.ssoAuthUrl || '')}"
          data-client-id="${esc(ds.ssoClientId || '')}"
          data-idp="${esc(ds.ssoIdp || '')}"
          data-response-type="${esc(ds.ssoResponseType || '')}"
          data-scopes="${esc(ds.ssoScopes || '')}"
          data-state="${esc(ds.ssoState || '')}"
          data-redirect-uri="${esc(ds.ssoRedirectUri || '')}"
        >
          ${cmrpGetSsoLandingBtnFragmentHtml('badge')}
          <span class="cmrp-student-shop-btn__text">${esc(btnLabel)}</span>
          ${cmrpGetSsoLandingBtnFragmentHtml('arrow')}
        </button>
        <p class="cmrp-sso-landing-panel__manual">
          <span class="cmrp-sso-landing-panel__manual-prefix">Or add courses manually to create list</span>
          <button type="button" class="cmrp-sso-landing-panel__manual-link" data-cmrp-sso-manual="true">Shop By Course</button>
        </p>
      </div>`;
}

class CourseMaterialsSidebar {
  constructor(config = {}) {
    this.config = {
      ddcs_campus_entity: config.ddcs_campus_entity || [],
      ddcs_campus_division: config.ddcs_campus_division || [],
      ddcs_term: config.ddcs_term || [],
      ddcs_campus_department: config.ddcs_campus_department || [],
      ddcs_course: config.ddcs_course || [],
      ddcs_section: config.ddcs_section || [],
      selectedFilters: config.selectedFilters || [],
      container: config.container || document,
      ...config,
    };

    this.campus_entity_id = '';
    this.term_id = '';
    this.campus_department_id = '';
    this.course_id = '';
    this.division_id = '';
    this.show_division = false;
    this.previousCampusValue = '';
    this.isEditingCampusTerm = false;

    this.init();
  }

  init() {
    this.bindEvents();
    this.initializeSkipSection();
    this.populateCampus();
    this.initializeAllCoursesList();
    this.initializeMobileTrigger();
    this._scheduleQueryCampusTermHydration();
    this.initializeMobileDrawerOnLoad();
    window.courseMaterialsSidebar = this;

    syncCmrpTypedStudentIdDomFlags();
    const runSyncLanding = () => this.syncCmrpSsoLandingPanelFromSession();
    queueMicrotask(runSyncLanding);
    setTimeout(runSyncLanding, 0);
    setTimeout(runSyncLanding, 150);

    if (!window.__cmrpCampusReadySsoLandingRelayout) {
      window.__cmrpCampusReadySsoLandingRelayout = true;
      document.addEventListener('cmrp:campusTermDataReady', () => {
        window.syncCmrpSsoLandingPanelFromSession?.();
      });
    }
  }

  reassertCampusTermUiAfterSsoLandingClear() {
    const filteredCampus = (this.config?.ddcs_campus_entity || []).filter(
      c => !c.disabled_flag,
    );
    this.maybeMarkCourseDetailsSectionCampusUiReady(filteredCampus.length);
  }

  syncCmrpSsoLandingPanelFromSession() {
    const filters = document.getElementById('filters-section');
    const hintMount = document.getElementById('cmrp-sso-landing-hint');
    const glassPanel = document.getElementById('cmrp-sso-landing-panel');
    const show = cmrpShouldShowSsoFirstLanding();

    const clearSsoLandingDom = () => {
      if (hintMount) {
        hintMount.innerHTML = '';
        hintMount.hidden = true;
      }
      if (glassPanel) {
        glassPanel.innerHTML = '';
        glassPanel.hidden = true;
      }
    };

    if (!show) {
      const hadSsoLanding = filters?.classList.contains('cmrp-sso-landing-active') === true;
      filters?.classList.remove('cmrp-sso-landing-active');
      clearSsoLandingDom();
      if (hadSsoLanding) {
        this.reassertCampusTermUiAfterSsoLandingClear();
      }
      window.applyCmrpCourseDetailsSectionFlowUi?.();
      syncCmrpTypedStudentIdDomFlags();
      return;
    }

    filters?.classList.add('cmrp-sso-landing-active');

    const glassHtml = cmrpBuildSsoLandingGlassHtml(filters?.dataset);
    const persistStudentFlow = () => {
      if (typeof window.setCmrpInitialFlowPreference === 'function') {
        window.setCmrpInitialFlowPreference('student-id');
      }
      cmrpPersistFlow('student-id');
    };

    if (hintMount) {
      hintMount.innerHTML = CMRP_SSO_LANDING_HINT_HTML;
      hintMount.hidden = false;
    }

    if (glassPanel) {
      glassPanel.innerHTML = glassHtml;
      glassPanel.hidden = false;
      glassPanel
        .querySelector('[data-sso-button="true"]')
        ?.addEventListener('click', persistStudentFlow, true);
      glassPanel
        .querySelector('[data-cmrp-sso-manual="true"]')
        ?.addEventListener('click', () => window.scrollCmrpToCourseBuilder?.(), true);
    }

    window.applyCmrpCourseDetailsSectionFlowUi?.();
    syncCmrpTypedStudentIdDomFlags();
  }

  getElementById(id) {
    // Canonical DDCS selects: desktop row for the active CMRP flow (see cmrp-filter-form suffixes).
    if (id === 'campus' || id === 'term') {
      const root = cmrpCampusTermRootId('activeCt');
      const elId = cmrpId(id, 'activeCt');
      const canonical = document.querySelector(`#${root} #${elId}`);
      if (canonical) return canonical;
    }
    const cbOnly = new Set([
      'division',
      'department',
      'course',
      'section',
      'division_field',
      'add-course',
      'selected-courses-container',
      'cmrp-course-details-heading',
    ]);
    if (cbOnly.has(id)) {
      const el = document.getElementById(cmrpId(id, 'cb'));
      if (el) return el;
    }
    const sidOnly = new Set([
      'student-id',
      'submit-student-id',
      'student-id-info-desktop',
      'student-id-submit-container-desktop',
    ]);
    if (sidOnly.has(id)) {
      const el = document.getElementById(cmrpId(id, 'sid'));
      if (el) return el;
    }
    if (id === 'selected-campus-term-text') {
      const el = document.getElementById(cmrpId('selected-campus-term-text', 'activeCt'));
      if (el) return el;
    }
    if (this.config.container && this.config.container !== document) {
      return this.config.container.querySelector(`#${id}`);
    }
    return document.getElementById(id);
  }

  querySelector(selector) {
    return this.config.container.querySelector(selector);
  }

  querySelectorAll(selector) {
    return this.config.container.querySelectorAll(selector);
  }

  /**
   * Refresh mobile drawer campus/term labels and submit state after <select> options change.
   */
  syncMobileCampusTermDisplays() {
    if (!window.courseMaterialsMobile) return;
    const m = window.courseMaterialsMobile;
    if (typeof m.updateCampusDisplay === 'function') {
      m.updateCampusDisplay();
    }
    if (typeof m.updateTermDisplay === 'function') {
      m.updateTermDisplay();
    }
    if (
      m.currentMode === 'studentid' &&
      typeof m.updateMobileSubmitButtonStateForStudentId === 'function'
    ) {
      m.updateMobileSubmitButtonStateForStudentId();
    } else if (typeof m.updateMobileSubmitButtonState === 'function') {
      m.updateMobileSubmitButtonState();
    }
  }

  /**
   * True when we're in shop-by-student-id flow (URL param or UI mode).
   * Used to avoid clearing results/URL when populateTerms/populateDepartmentsFromTerms run.
   */
  isStudentIdMode() {
    const f = cmrpCurrentFlow();
    if (f === 'student-id' || f === 'sso') return true;
    const filtersPanel = document.getElementById('filters-section');
    return filtersPanel?.classList.contains('student-id-mode') === true;
  }

  bindEvents() {
    this.config.container
      .getElementById?.('open-filters')
      ?.addEventListener('click', e => {
        e.preventDefault();
        this.openSidePanel();
      }) ||
      document.getElementById('open-filters')?.addEventListener('click', e => {
        e.preventDefault();
        this.openSidePanel();
      });

    this.config.container
      .getElementById?.('close-filters')
      ?.addEventListener('click', e => {
        e.preventDefault();
        this.closeSidePanel();
      }) ||
      document.getElementById('close-filters')?.addEventListener('click', e => {
        e.preventDefault();
        this.closeSidePanel();
      });

    const cbForm = document.querySelector('.cmrp-filter-form--course-builder');
    if (cbForm) {
      cbForm.addEventListener('change', e => {
        if (e.target instanceof HTMLSelectElement) {
          this.updateAddCourseButtonState();
        }
      });
    }

    this.getElementById('add-course')?.addEventListener('click', async () =>
      await this.handleAddCourse()
    );
    // Removed add-student event listener
    this.getElementById('submit-student-id')?.addEventListener('click', () =>
      this.handleAddStudent()
    );
    const studentIdField = this.getElementById('student-id');
    if (studentIdField) {
      // Remove the automatic input event listener to prevent unwanted search logic
      // Student ID will only be processed when SUBMIT button is clicked
    }
  }

  initializeSkipSection() {
    let retryCount = 0;
    const maxRetries = 10;

    const initSkip = () => {
      const skipText = document.querySelector('.skip-text');
      const skipOptions = document.querySelector('.skip-options');
      const chevronIcon = document.querySelector('.chevron-down-icon');

      if (!skipText || !skipOptions || !chevronIcon) {
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(initSkip, 200);
        }
        return;
      }

      chevronIcon.style.transform = 'rotate(180deg)';
      skipOptions.style.display = 'none';

      skipText.onclick = function () {
        const currentSkipOptions = document.querySelector('.skip-options');
        const currentChevronIcon = document.querySelector('.chevron-down-icon');

        if (currentSkipOptions && currentChevronIcon) {
          const isHidden =
            currentSkipOptions.style.display === 'none' ||
            getComputedStyle(currentSkipOptions).display === 'none';

          if (isHidden) {
            currentSkipOptions.style.display = 'block';
            currentChevronIcon.style.transform = 'rotate(0deg)';
          } else {
            currentSkipOptions.style.display = 'none';
            currentChevronIcon.style.transform = 'rotate(180deg)';
          }
        }
      };
    };

    initSkip();
    
    // Initialize panel button states
    this.initializePanelButtons();
  }

  // Initialize panel button states
  initializePanelButtons() {
    const openFiltersBtn = document.getElementById('open-filters');
    const closeFiltersBtn = document.getElementById('close-filters');
    
    // Initially, sidebar is closed, so show open button and hide close button
    if (openFiltersBtn) openFiltersBtn.style.display = 'none';
    if (closeFiltersBtn) closeFiltersBtn.style.display = 'none';
  }

  // Panel Controls
  openSidePanel() {
    const filtersSection = document.getElementById('filters-section');
    const separatorLine = document.getElementById('separator-line');
    const openFiltersBtn = document.getElementById('open-filters');
    const closeFiltersBtn = document.getElementById('close-filters');

    if (filtersSection) filtersSection.style.display = 'block';
    if (separatorLine) separatorLine.style.display = 'block';
    if (openFiltersBtn) openFiltersBtn.style.display = 'none';
    if (closeFiltersBtn) closeFiltersBtn.style.display = 'none';
    
    document.body.classList.add('sidebar-open');
    // Match initial load: synchronous reparent while #loading-state is visible can trigger
    // browser scroll anchoring (viewport jumps toward the footer), then post-restore scroll targets results.
    scheduleCmrpAnchorProductsPanel();
  }

  closeSidePanel() {
    const filtersSection = document.getElementById('filters-section');
    const separatorLine = document.getElementById('separator-line');
    const openFiltersBtn = document.getElementById('open-filters');
    const closeFiltersBtn = document.getElementById('close-filters');

    restoreCmrpProductsToDefaultSlot();
    if (filtersSection) filtersSection.style.display = 'none';
    if (separatorLine) separatorLine.style.display = 'none';
    if (openFiltersBtn) openFiltersBtn.style.display = 'block';
    if (closeFiltersBtn) closeFiltersBtn.style.display = 'none';
    
    document.body.classList.remove('sidebar-open');

    // Finish campus/term editing when closing the panel
    this.finishCampusTermEdit();
  }

  /**
   * Duplicate id="campus" / id="term" exist (filters panel + mobile drawer). Desktop row is canonical;
   * mirror options and values so mobile hidden selects and pickers stay in sync.
   */
  syncMobileDrawerCampusTermSelects() {
    // Both forms use different desktop rows; copying "activeCt" would cross-wire them.
    if (cmrpShowBothForms()) return;

    const deskCampus = cmrpQueryDesktopCampusTerm('campus');
    const mobCampus = document.querySelector('#mobile-campus-term-dropdowns #campus');
    if (deskCampus && mobCampus && deskCampus !== mobCampus) {
      mobCampus.innerHTML = deskCampus.innerHTML;
      mobCampus.disabled = deskCampus.disabled;
      mobCampus.value = deskCampus.value;
    }
    const deskTerm = cmrpQueryDesktopCampusTerm('term');
    const mobTerm = document.querySelector('#mobile-campus-term-dropdowns #term');
    if (deskTerm && mobTerm && deskTerm !== mobTerm) {
      mobTerm.innerHTML = deskTerm.innerHTML;
      mobTerm.disabled = deskTerm.disabled;
      mobTerm.value = deskTerm.value;
    }
  }

  // Campus and Term Management

  /**
   * Desktop: reveal .course-details-section only after campus list is trustworthy.
   * CMRP starts with empty campuses then merges API data — skip until __cmrpCampusTermFetchApplied.
   */
  maybeMarkCourseDetailsSectionCampusUiReady(filteredCampusCount) {
    const fetchApplied = window.__cmrpCampusTermFetchApplied === true;
    if (!fetchApplied && filteredCampusCount === 0) return;
    document
      .querySelectorAll('.course-details-section')
      .forEach(el => el.classList.add('course-details-section--campus-ui-ready'));
  }

  /**
   * Student ID flow: when campus row is hidden (single campus), prompt for term only.
   * @param {boolean} singleCampusMode
   * @param {number} [filteredCampusCount] — used with __cmrpCampusTermFetchApplied so mobile hint stays hidden until CMRP campus API has merged.
   */
  updateStudentIdScheduleHint(singleCampusMode, filteredCampusCount = 0) {
    const sidInfo = document.getElementById(cmrpId('student-id-info-desktop', 'sid'));
    const desktop = sidInfo?.querySelector('.student-id-text');
    const mobile = document.querySelector('#mobile-student-id-section .mobile-student-id-text');
    const mobileInfo = document.querySelector('#mobile-student-id-section .mobile-student-id-info');
    if (desktop) {
      desktop.textContent = singleCampusMode
        ? 'Select Term to pull your schedule'
        : 'Select Campus and Term to pull your schedule';
    }
    if (mobile) {
      mobile.textContent = singleCampusMode
        ? 'Select Term to pull your schedule'
        : 'Enter your Course and Term to pull in your schedule';
    }
    const fetchApplied = window.__cmrpCampusTermFetchApplied === true;
    if (fetchApplied || filteredCampusCount > 0) {
      mobileInfo?.classList.add('mobile-student-id-info--message-ready');
    }
  }

  /**
   * @param {boolean} preserveSelection
   * @param {'cb' | 'sid' | null} onlyRowScope - When both CMRP forms are shown, repopulate only this campus/term row (null = all rows).
   */
  populateCampus(preserveSelection = false, onlyRowScope = null) {
    const filteredCampus = this.config.ddcs_campus_entity.filter(
      campus => !campus.disabled_flag
    );
    const filteredCampusCount = filteredCampus.length;

    const { cb, sid } = cmrpSuffixPair();
    const dual = Boolean(cb && sid && cmrpShowBothForms());
    const campusTargets = [];
    if (dual) {
      const cCb = document.getElementById(cmrpId('campus', 'cb'));
      const cSid = document.getElementById(cmrpId('campus', 'sid'));
      if (cCb) campusTargets.push({ el: cCb, rowScope: 'cb' });
      if (cSid) campusTargets.push({ el: cSid, rowScope: 'sid' });
    } else {
      const c = this.getElementById('campus');
      if (c) campusTargets.push({ el: c, rowScope: null });
    }

    if (campusTargets.length === 0) {
      this.maybeMarkCourseDetailsSectionCampusUiReady(filteredCampusCount);
      this.updateStudentIdScheduleHint(filteredCampusCount === 1, filteredCampusCount);
      return;
    }

    const resetFormGroupStyles = el => {
      if (!el) return;
      el.style.display = '';
      el.style.visibility = '';
      el.style.height = '';
      el.style.overflow = '';
      el.style.margin = '';
      el.style.padding = '';
    };

    if (filteredCampusCount > 0) {
      for (const { el: campusSelect, rowScope } of campusTargets) {
        if (onlyRowScope != null && rowScope !== onlyRowScope) {
          continue;
        }
        const currentValue = preserveSelection ? campusSelect.value : '';
        campusSelect.innerHTML = '<option value="">Campus</option>';
        campusSelect.disabled = false;
        filteredCampus.forEach(campus => {
          const option = document.createElement('option');
          const label = decodeHtmlEntities(campus.name);
          option.value = label;
          cmrpStampOptionFullLabel(option, label);
          campusSelect.appendChild(option);
        });

        const rootId = rowScope
          ? cmrpCampusTermRootId(rowScope)
          : cmrpCampusTermRootId('activeCt');
        const ctRootEl = document.getElementById(rootId);
        const desktopCampusFormGroup = ctRootEl?.querySelector(
          '.form-group:first-child',
        );

        if (filteredCampus.length === 1) {
          // One campus: auto-select and hide the campus control on every row (course-builder + student-id).
          campusSelect.value = decodeHtmlEntities(filteredCampus[0].name);
          const studentInlineRoot = ctRootEl?.classList?.contains(
            'cmrp-student-inline-row__inner',
          );
          const skipSingleCampusHide =
            Boolean(this.isEditingCampusTerm) && studentInlineRoot;
          if (desktopCampusFormGroup && !skipSingleCampusHide) {
            if (studentInlineRoot) {
              desktopCampusFormGroup.style.removeProperty('display');
            } else {
              desktopCampusFormGroup.style.display = 'none';
            }
          } else if (skipSingleCampusHide) {
            resetFormGroupStyles(desktopCampusFormGroup);
          }
          if (studentInlineRoot && !skipSingleCampusHide) {
            ctRootEl.dataset.cmrpCampusUi = 'single';
          } else if (studentInlineRoot && skipSingleCampusHide) {
            delete ctRootEl.dataset.cmrpCampusUi;
          }
          if (!preserveSelection) {
            this.populateTerms(campusSelect);
          }
        } else {
          resetFormGroupStyles(desktopCampusFormGroup);
          if (ctRootEl?.classList?.contains('cmrp-student-inline-row__inner')) {
            delete ctRootEl.dataset.cmrpCampusUi;
          }
          if (preserveSelection && currentValue) {
            campusSelect.value = currentValue;
          }
        }
      }

      if (onlyRowScope == null) {
        const mobileCampusFormGroup = document.querySelector(
          '#mobile-campus-term-dropdowns .mobile-form-group:first-child',
        );
        if (filteredCampus.length === 1) {
          if (mobileCampusFormGroup) {
            mobileCampusFormGroup.style.display = 'none';
          }
          const mobileCampusSelect = document.querySelector(
            '#mobile-campus-term-dropdowns #campus',
          );
          if (mobileCampusSelect) {
            mobileCampusSelect.value = decodeHtmlEntities(filteredCampus[0].name);
          }
        } else {
          resetFormGroupStyles(mobileCampusFormGroup);
        }

        if (!preserveSelection && filteredCampus.length === 1) {
          if (
            window.courseMaterialsMobile &&
            typeof window.courseMaterialsMobile.updateCampusDisplay === 'function'
          ) {
            window.courseMaterialsMobile.updateCampusDisplay();
          }
        }
      }

      this.syncMobileDrawerCampusTermSelects();
      cmrpMirrorCampusTermBetweenForms();
    } else {
      this.disableSelects([
        'campus',
        'term',
        'department',
        'course',
        'section',
      ]);
    }

    this.showCampusTermDropdowns();
    this.maybeMarkCourseDetailsSectionCampusUiReady(filteredCampusCount);
    this.updateStudentIdScheduleHint(filteredCampusCount === 1, filteredCampusCount);
  }
  async checkSession() {
    const token = sessionStorage.getItem('sessionToken');
    
    // Return immediately if the token does not exist
    if (!token) return false;

    try {
      const response = await fetch('/apps/shopifyData/session/check', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Check if the response is not successful
      if (!response.ok) throw new Error(`Error: ${response.status}`);

      // Return the session data
      const data = await response.json();
      return data;
      
    } catch (error) {
      console.error('Error checking session:', error);
    }
  }
  /**
   * @param {HTMLSelectElement} [fromCampusSelect] - Pass `this` from inline onchange so campus/term
   *   updates stay on the same row when both cb and sid forms exist (otherwise activeCt can mismatch).
   */
  async populateTerms(fromCampusSelect) {
    const campusSelect =
      fromCampusSelect && fromCampusSelect.tagName === 'SELECT'
        ? fromCampusSelect
        : this.getElementById('campus');
    if (!campusSelect) return;

    const scope = cmrpScopeFromCtSelect(campusSelect);
    const termSelect = document.getElementById(cmrpId('term', scope));
    if (!termSelect) return;

    const session_check = await this.checkSession();
    let studentId = false;
    if (session_check && session_check.status) {
      studentId = session_check.sessionID;
    }
    if (studentId) {
      if (!window.shopifySettings.studentIDActivation) {
        this.showCourseSelectionFields();
      }
    } else {
      if (!window.shopifySettings.studentIDActivation) {
        this.showCourseSelectionFields();
      }
    }

    const selectedCampus = campusSelect.value;

    await this.completeCampusChangeForRow(
      selectedCampus,
      campusSelect,
      termSelect,
    );
  }

  /**
   * Populate terms while preserving a specific term selection
   * @param {string} termToPreserve - The term value to preserve after population
   * @param {'cb'|'sid'} [rowScope] - When both forms exist, which campus/term row to update (default: course-builder cb).
   */
  populateTermsPreservingSelection(termToPreserve = '', rowScope) {
    const { cb, sid } = cmrpSuffixPair();
    let campusSelect;
    let termSelect;
    if (rowScope === 'sid' && sid) {
      campusSelect = document.getElementById(cmrpId('campus', 'sid'));
      termSelect = document.getElementById(cmrpId('term', 'sid'));
    } else if (cb) {
      campusSelect = document.getElementById(cmrpId('campus', 'cb'));
      termSelect = document.getElementById(cmrpId('term', 'cb'));
    } else {
      campusSelect = this.getElementById('campus');
      termSelect = this.getElementById('term');
    }
    if (!campusSelect || !termSelect) return;

    const selectedCampus = campusSelect.value;
    termSelect.innerHTML = '<option value="">Term</option>';

    if (selectedCampus) {
      const normalize = value =>
        String(value ?? '')
          .toLowerCase()
          .replace(/\s+/g, '');

      const filteredCampus = this.config.ddcs_campus_entity.filter(campus =>
        normalizeDdcsLabel(campus.name).includes(normalizeDdcsLabel(selectedCampus))
      );

      if (filteredCampus.length > 0) {
        const entityIdForTerms = filteredCampus[0].campus_entity_id;
        const sidIsolated = cmrpIsolateStudentIdCampusTermFromCourseBuilder(
          rowScope === 'sid' ? 'sid' : 'cb',
        );
        if (!sidIsolated) {
          this.campus_entity_id = entityIdForTerms;
          this.show_division = filteredCampus[0].is_division_used_flag;
          this.setupDivisionField();
        }

        const filteredTerms = window.courseData.ddcs_term.filter(
          term =>
            normalize(term.campus_entity_id) === normalize(entityIdForTerms),
        );

        if (filteredTerms.length > 0) {
          termSelect.disabled = false;
          filteredTerms.forEach(term => {
            const option = document.createElement('option');
            option.value = term.term_id;
            cmrpStampOptionFullLabel(option, decodeHtmlEntities(term.name));
            termSelect.appendChild(option);
          });

          // Restore the term selection if provided
          if (termToPreserve) {
            termSelect.value = termToPreserve;
            // Update the term_id for the preserved selection
            const filteredTerm = window.courseData.ddcs_term.filter(
              term => normalize(term.term_id) === normalize(termToPreserve),
            );
            if (filteredTerm.length > 0 && !sidIsolated) {
              this.term_id = filteredTerm[0].term_id;
            }
          }
        } else {
          if (rowScope === 'sid') {
            termSelect.innerHTML = '<option value="">Term</option>';
            termSelect.disabled = true;
          } else {
            this.disableSelects(['term', 'department', 'course', 'section']);
          }
        }
      } else {
        if (rowScope === 'sid') {
          termSelect.innerHTML = '<option value="">Term</option>';
          termSelect.disabled = true;
        } else {
          this.disableSelects(['term', 'department', 'course', 'section']);
        }
      }
    } else {
      if (rowScope === 'sid') {
        termSelect.innerHTML = '<option value="">Term</option>';
        termSelect.disabled = true;
      } else {
        this.disableSelects(['term', 'department', 'course', 'section']);
        this.hideDivisionField();
      }
    }

    this.syncMobileDrawerCampusTermSelects();
    cmrpMirrorCampusTermBetweenForms();
    this.syncMobileCampusTermDisplays();
  }
  async fetchMetaobjects(metaobjectType, query) {
    if (typeof window !== 'undefined' && window.fetchMetaobjectsOnce) {
      return window.fetchMetaobjectsOnce(metaobjectType, query);
    }

    const proxyPath = '/apps/shopifyData/metaobjects';
    const url = `${proxyPath}?type=${metaobjectType}&query=${query}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        const activeMetaobjects = result.data.filter(
          item => item.status === 'ACTIVE',
        );
        return activeMetaobjects;
      } else {
        throw new Error(result.error || 'Failed to fetch metaobjects');
      }
    } catch (error) {
      console.error('Error fetching metaobjects:', error);
      throw error;
    }
  }
  async fetchMetaobjectsData(termId, campusId) {
    // Usa helper global com cache quando disponível
    if (typeof window !== 'undefined' && window.fetchFilteredDataOnce) {
      return window.fetchFilteredDataOnce(termId, campusId);
    }

    // Fallback para implementação antiga (caso helper não esteja disponível)
    const proxyPath = '/apps/shopifyData/getFilteredData';
    const url = `${proxyPath}?campus_entity_id=${campusId}&term_id=${termId}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to fetch metaobjects');
      }
    } catch (error) {
      console.error('Error fetching metaobjects:', error);
      throw error;
    }
  }

  /**
   * Load campus/term DDCS metaobjects for a campus/term row.
   * By default writes into `window.courseData` (division/dept/course/section) for ProductDisplayManager.
   * When **both** student-id and course-builder forms exist and `rowScope === 'sid'`, that would
   * clobber the course-builder catalog — so we only write the shared `window.courseData` quartet
   * when `opts.allowSharedCourseDataOverwrite` is true (e.g. after student discovery, before materials refresh).
   * Otherwise sid data is stored on `window.courseData.__cmrpStudentIdRowDdcs` only.
   * @param {'sid' | 'cb'} rowScope
   * @param {{ allowSharedCourseDataOverwrite?: boolean }} [opts]
   */
  async hydrateDdcsDataForCampusTermRow(rowScope, opts = {}) {
    const campusSelect = document.getElementById(cmrpId('campus', rowScope));
    const termSelect = document.getElementById(cmrpId('term', rowScope));
    if (!campusSelect || !termSelect) return;

    const normalize = value =>
      String(value ?? '')
        .toLowerCase()
        .replace(/\s+/g, '');

    let campusEntityId = '';
    let termId = '';

    const campusName = campusSelect.value;
    if (campusName) {
      const filteredCampus = this.config.ddcs_campus_entity.filter(campus =>
        normalizeDdcsLabel(campus.name).includes(normalizeDdcsLabel(campusName)),
      );
      if (filteredCampus.length > 0) {
        campusEntityId = filteredCampus[0].campus_entity_id;
        const showDiv = filteredCampus[0].is_division_used_flag;
        if (!cmrpIsolateStudentIdCampusTermFromCourseBuilder(rowScope)) {
          this.campus_entity_id = campusEntityId;
          this.show_division = showDiv;
        }
      }
    }

    const selectedTerm = String(termSelect.value || '').trim();
    if (selectedTerm) {
      termId = selectedTerm;
      if (!cmrpIsolateStudentIdCampusTermFromCourseBuilder(rowScope)) {
        this.term_id = selectedTerm;
      }
    }

    if (!campusEntityId || !termId) {
      return;
    }

    const dataFilters = await this.fetchMetaobjectsData(termId, campusEntityId);

    const dualBothForms = cmrpShowBothForms();
    const sidRow = rowScope === 'sid';
    if (
      dualBothForms &&
      sidRow &&
      opts.allowSharedCourseDataOverwrite !== true
    ) {
      window.courseData.__cmrpStudentIdRowDdcs = {
        ddcs_campus_division: dataFilters.ddcs_campus_division,
        ddcs_campus_department: dataFilters.ddcs_campus_department,
        ddcs_course: dataFilters.ddcs_course,
        ddcs_section: dataFilters.ddcs_section,
      };
      return;
    }

    window.courseData.ddcs_campus_division = [];
    window.courseData.ddcs_campus_department = [];
    window.courseData.ddcs_course = [];
    window.courseData.ddcs_section = [];

    window.courseData.ddcs_campus_division = dataFilters.ddcs_campus_division;
    window.courseData.ddcs_campus_department = dataFilters.ddcs_campus_department;
    window.courseData.ddcs_course = dataFilters.ddcs_course;
    window.courseData.ddcs_section = dataFilters.ddcs_section;
  }

  /**
   * Complete the campus change after confirmation (or if no confirmation needed)
   * @param {string} selectedCampus - The selected campus value
   */
  async completeCampusChange(selectedCampus) {
    const cbSuf = cmrpSuffixPair().cb;
    const campusSelect = cbSuf
      ? document.getElementById(cmrpId('campus', 'cb'))
      : this.getElementById('campus');
    if (!campusSelect) return;
    const scope = cmrpScopeFromCtSelect(campusSelect);
    const termSelect = document.getElementById(cmrpId('term', scope));
    if (!termSelect) return;
    campusSelect.value = selectedCampus;
    await this.completeCampusChangeForRow(
      selectedCampus,
      campusSelect,
      termSelect,
    );
  }

  /**
   * Apply campus selection to a specific campus/term row (cb vs sid).
   */
  async completeCampusChangeForRow(selectedCampus, campusSelect, termSelect) {
    if (!campusSelect || !termSelect) return;

    campusSelect.value = selectedCampus;
    this.previousCampusValue = selectedCampus;

    const rowScope = cmrpScopeFromCtSelect(campusSelect);
    const skipFullClear =
      rowScope === 'sid' ||
      (this.isStudentIdMode() && !cmrpShowBothForms());
    if (!skipFullClear) {
      this.clearAllUserSelections(false, true);
    }

    const preserveTermInStudentIdMode =
      (rowScope === 'sid' || this.isStudentIdMode()) && termSelect.value;
    termSelect.innerHTML = '<option value="">Term</option>';

    if (selectedCampus) {
      const normalize = value =>
        String(value ?? '')
          .toLowerCase()
          .replace(/\s+/g, '');

      const filteredCampus = this.config.ddcs_campus_entity.filter(campus =>
        normalizeDdcsLabel(campus.name).includes(normalizeDdcsLabel(selectedCampus))
      );

      if (filteredCampus.length > 0) {
        const entityIdForTerms = filteredCampus[0].campus_entity_id;
        // Course-builder globals drive the cb DDCS row only. Student-id row must not
        // reset division visibility or entity ids used by course-builder when both forms show.
        if (!cmrpIsolateStudentIdCampusTermFromCourseBuilder(rowScope)) {
          this.campus_entity_id = entityIdForTerms;
          this.show_division = filteredCampus[0].is_division_used_flag;
          this.setupDivisionField();
        }

        const filteredTerms = window.courseData.ddcs_term.filter(
          term =>
            normalize(term.campus_entity_id) === normalize(entityIdForTerms),
        );

        const sortValuesByName = (a, b) => {
          const nameA = decodeHtmlEntities(a.name);
          const nameB = decodeHtmlEntities(b.name);

          const isNumA = !isNaN(nameA.charAt(0));
          const isNumB = !isNaN(nameB.charAt(0));
          
          if (isNumA && isNumB) {
            return parseInt(nameA) - parseInt(nameB); 
          }
          
          if (isNumA) {
            return -1; 
          }
          
          if (isNumB) {
            return 1; 
          }
          
          return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
        };
        const sortedValues = filteredTerms.sort(sortValuesByName);
        
        if (filteredTerms.length > 0) {
          if(filteredTerms.length == 1){
            termSelect.disabled = false;
            filteredTerms.forEach(term => {
              const option = document.createElement('option');
              option.value = term.term_id;
              cmrpStampOptionFullLabel(option, decodeHtmlEntities(term.name));
              option.selected = true;
              termSelect.appendChild(option);
            });
            await this.populateDepartmentsFromTerms(termSelect);
          }else{
            termSelect.disabled = false;
            filteredTerms.forEach(term => {
              const option = document.createElement('option');
              option.value = term.term_id;
              cmrpStampOptionFullLabel(option, decodeHtmlEntities(term.name));
              termSelect.appendChild(option);
            });
          }
          if (preserveTermInStudentIdMode && termSelect.querySelector('option[value="' + preserveTermInStudentIdMode + '"]')) {
            termSelect.value = preserveTermInStudentIdMode;
          }
        } else {
          this._disableTermRowForCompleteCampus(termSelect);
        }
      } else {
        this._disableTermRowForCompleteCampus(termSelect);
      }
    } else {
      this._disableTermRowForCompleteCampus(termSelect);
      if (rowScope !== 'sid') {
        this.hideDivisionField();
      }
    }

    this.syncMobileDrawerCampusTermSelects();
    cmrpMirrorCampusTermBetweenForms();
    this.syncMobileCampusTermDisplays();
    this.updateAddCourseButtonState();
  }

  _disableTermRowForCompleteCampus(termSelect) {
    if (termSelect) {
      termSelect.innerHTML = '<option value="">Term</option>';
      termSelect.disabled = true;
    }
    if (cmrpShowBothForms() && cmrpScopeFromCtSelect(termSelect) === 'sid') {
      return;
    }
    if (cmrpShowBothForms()) {
      this.disableSelects(['department', 'course', 'section']);
    } else {
      this.disableSelects(['term', 'department', 'course', 'section']);
    }
  }

  /**
   * @param {HTMLSelectElement} [fromTermSelect] - Pass `this` from inline onchange for the correct row when cb+sid both exist.
   * @param {{ skipPageWideTermDeptReset?: boolean }} [opts] - When true on course-builder (`cb`), skip `clearAllTermSelections`
   *   (avoids `setURLSearchParams` + empty-state churn during `?campus=`/`?term=` hydration so `termSelect.value` stays committed).
   */
  async populateDepartmentsFromTerms(fromTermSelect, opts = {}) {
    // Division/department/course/section are always on the course-builder row (`-cb` when dual-form).
    // Do not default to `cmrpQueryDesktopCampusTerm('term')` — that follows `activeCt`, which can be
    // the student-id row while `?campus=` / `?term=` hydrated the cb row, leaving division disabled.
    const termSelect =
      fromTermSelect && fromTermSelect.tagName === 'SELECT'
        ? fromTermSelect
        : cmrpCourseBuilderCampusTermEl('term');
    if (!termSelect) {
      cmrpDdcsDebug('populateDepartmentsFromTerms.abort', {
        reason: 'no termSelect (course-builder)',
      });
      return;
    }

    const rowScope = cmrpScopeFromCtSelect(termSelect);
    const skipPageWideReset =
      opts?.skipPageWideTermDeptReset === true && rowScope === 'cb';

    cmrpDdcsDebug('populateDepartmentsFromTerms.enter', {
      passedSelectId: fromTermSelect?.id || null,
      resolvedSelectId: termSelect.id,
      resolvedValue: termSelect.value,
      selectedIndex: termSelect.selectedIndex,
      selectedText:
        termSelect.options[termSelect.selectedIndex]?.textContent?.trim?.() ||
        '',
      rowScope,
      skipPageWideReset,
      dualForms: cmrpShowBothForms(),
      activeCtSuffix: cmrpActiveCampusTermSuffix(),
    });

    const session_check = await this.checkSession();
    let studentId = false;
    if (session_check && session_check.status) {
      studentId = session_check.sessionID;
    }
    if (studentId) {
      if (!window.shopifySettings.studentIDActivation) {
        this.showCourseSelectionFields();
      }
    } else {
      if (!window.shopifySettings.studentIDActivation) {
        this.showCourseSelectionFields();
      }
    }

    // When both CMRP forms render, student-id campus/term must not touch the course-builder row
    // or overwrite `window.courseData` (shop-by-course uses that for dept/course/section options).
    if (cmrpShowBothForms() && rowScope === 'sid') {
      cmrpDdcsDebug('populateDepartmentsFromTerms.sidEarlyReturn', {
        message:
          'dual CMRP: student-id row only — no shared courseData hydrate (avoids clobbering course-builder DDCS).',
      });
      this.updateAddCourseButtonState();
      return;
    }

    if (skipPageWideReset) {
      this.clearDDCSSelections(false);
    } else if (
      rowScope === 'sid' ||
      (this.isStudentIdMode() && !cmrpShowBothForms())
    ) {
      this.clearDDCSSelections();
    } else {
      this.clearAllTermSelections(true);
    }
    this.disableSelects(['division', 'department', 'course', 'section']);

    const campusSelect = document.getElementById(cmrpId('campus', rowScope));
    const campusName = campusSelect?.value;
    if (campusName) {
      const normalize = value =>
        String(value ?? '')
          .toLowerCase()
          .replace(/\s+/g, '');
      const campusPool =
        (Array.isArray(this.config.ddcs_campus_entity) &&
          this.config.ddcs_campus_entity.length > 0 &&
          this.config.ddcs_campus_entity) ||
        window.courseData?.ddcs_campus_entity ||
        [];
      const filteredCampus = campusPool.filter(campus =>
        normalizeDdcsLabel(campus.name).includes(normalizeDdcsLabel(campusName)),
      );
      if (filteredCampus.length > 0) {
        this.campus_entity_id = filteredCampus[0].campus_entity_id;
        this.show_division = filteredCampus[0].is_division_used_flag;
      }
    }

    const selectedTerm = String(
      termSelect.value ||
        termSelect.options[termSelect.selectedIndex]?.value ||
        '',
    ).trim();
    if (selectedTerm) {
      this.term_id = selectedTerm;
    }
    cmrpDdcsDebug('populateDepartmentsFromTerms.beforeFetch', {
      campusName,
      campus_entity_id: this.campus_entity_id,
      show_division: this.show_division,
      selectedTerm,
      term_id: this.term_id,
    });

    if (!selectedTerm) {
      console.warn('[CMRP DDCS] populateDepartmentsFromTerms: empty term value — division/dept will not load.', {
        termSelectId: termSelect.id,
        optionCount: termSelect.options.length,
      });
    }

    window.courseData.ddcs_campus_division = [];
    window.courseData.ddcs_campus_department = [];
    window.courseData.ddcs_course = [];
    window.courseData.ddcs_section = [];
    const dataFilters = await this.fetchMetaobjectsData(this.term_id, this.campus_entity_id);

    window.courseData.ddcs_campus_division = dataFilters.ddcs_campus_division;
    window.courseData.ddcs_campus_department = dataFilters.ddcs_campus_department;
    window.courseData.ddcs_course = dataFilters.ddcs_course;
    window.courseData.ddcs_section = dataFilters.ddcs_section;

    cmrpDdcsDebug('populateDepartmentsFromTerms.afterFetch', {
      divisionRows: (dataFilters.ddcs_campus_division || []).length,
      departmentRows: (dataFilters.ddcs_campus_department || []).length,
      courseRows: (dataFilters.ddcs_course || []).length,
      sectionRows: (dataFilters.ddcs_section || []).length,
    });

    //console.log("window.courseData ==> ", window.courseData)

    this.checkCampusTermSelection();

    if (selectedTerm) {
      this.setupDivisionField();
      if (this.show_division) {
        cmrpDdcsDebug('populateDepartmentsFromTerms.branch', {
          branch: 'populateDivision',
        });
        this.populateDivision();
      } else {
        cmrpDdcsDebug('populateDepartmentsFromTerms.branch', {
          branch: 'populateDepartments (no division flag on campus)',
        });
        this.populateDepartments();
      }
      
      // Load all courses when both campus and term are selected
      //this.loadAllCourses();
    } else {
      cmrpDdcsDebug('populateDepartmentsFromTerms.branch', {
        branch: 'skip (no selectedTerm)',
      });
    }
    this.updateAddCourseButtonState();
  }

  async populateDivision() {
    const divisionSelect = document.getElementById(cmrpId('division','cb'));
    if (!divisionSelect) {
      cmrpDdcsDebug('populateDivision.abort', { reason: 'no divisionSelect' });
      return;
    }

    divisionSelect.innerHTML = '<option value="">Division</option>';

    if (this.campus_entity_id) {
      const filteredDivisions = window.courseData.ddcs_campus_division;
      cmrpDdcsDebug('populateDivision', {
        divisionSelectId: divisionSelect.id,
        campus_entity_id: this.campus_entity_id,
        term_id: this.term_id,
        divisionCount: filteredDivisions.length,
        disabledBefore: divisionSelect.disabled,
      });
      if (filteredDivisions.length > 0) {
        if(filteredDivisions.length == 1){
          divisionSelect.disabled = false;
          filteredDivisions.forEach(division => {
            const option = document.createElement('option');
            option.value = division.campus_division_id;
            cmrpStampOptionFullLabel(option, decodeHtmlEntities(division.name));
            option.selected = true;
            divisionSelect.appendChild(option);
          });
          this.populateDepartments();
        }else{
          divisionSelect.disabled = false;
          filteredDivisions.forEach(division => {
            const option = document.createElement('option');
            option.value = division.campus_division_id;
            cmrpStampOptionFullLabel(option, decodeHtmlEntities(division.name));
            divisionSelect.appendChild(option);
          });
        }
        
      } else {
        divisionSelect.disabled = true;
        cmrpDdcsDebug('populateDivision.noRows', {
          message: 'API returned 0 divisions for this campus+term',
        });
      }
    } else {
      divisionSelect.disabled = true;
      cmrpDdcsDebug('populateDivision.noCampusEntity', {
        message: 'this.campus_entity_id missing',
      });
    }
    cmrpDdcsDebug('populateDivision.done', {
      divisionSelectId: divisionSelect.id,
      disabled: divisionSelect.disabled,
      optionCount: divisionSelect.options.length,
    });
    this.updateAddCourseButtonState();
  }

  async populateDepartments() {
    const departmentSelect = document.getElementById(cmrpId('department','cb'));
    const divisionSelect = document.getElementById(cmrpId('division','cb'));
    if (!departmentSelect) return;

    departmentSelect.innerHTML = '<option value="">Department</option>';

    // Clear child selects (course and section) when division changes
    const courseSelect = document.getElementById(cmrpId('course','cb'));
    const sectionSelect = document.getElementById(cmrpId('section','cb'));
    if (courseSelect) {
      courseSelect.innerHTML = '<option value="">Course</option>';
      courseSelect.disabled = true;
    }
    if (sectionSelect) {
      sectionSelect.innerHTML = '<option value="">Section</option>';
      sectionSelect.disabled = true;
    }

    const divisionField = document.getElementById(cmrpId('division_field','cb'));
    const hiddenDivision = divisionField?.hidden;

    if (hiddenDivision) {
      const filteredDepartments = window.courseData.ddcs_campus_department;
      
      this.populateDepartmentOptions(filteredDepartments, departmentSelect);
    } else {
      const selectedDivision = divisionSelect?.value;
      if (selectedDivision) {
        const normalize = value =>
          String(value).toLowerCase().replace(/\s+/g, '');
        const filteredDepartments = window.courseData.ddcs_campus_department.filter(div =>
          normalize(div.campus_division_id) === normalize(selectedDivision)
        );
        if (filteredDepartments.length > 0) {
          this.populateDepartmentOptions(filteredDepartments, departmentSelect);
        } 
      } else {
        this.disableSelects(['department', 'course', 'section']);
      }
    }
    this.updateAddCourseButtonState();
  }

  populateDepartmentOptions(departments, selectElement) {
    if (departments.length > 0) {
      if(departments.length == 1){
        selectElement.disabled = false;
        departments.forEach(department => {
          const option = document.createElement('option');
          option.value = department.campus_department_id;
          cmrpStampOptionFullLabel(option, decodeHtmlEntities(department.name));
          option.selected = true;
          selectElement.appendChild(option);
        });
        this.populateCourses();
      }else{
        selectElement.disabled = false;
        departments.forEach(department => {
          const option = document.createElement('option');
          option.value = department.campus_department_id;
          cmrpStampOptionFullLabel(option, decodeHtmlEntities(department.name));
          selectElement.appendChild(option);
        });
      }
      
    } else {
      this.disableSelects(['department', 'course', 'section']);
    }
  }

  async populateCourses() {
    const departmentSelect = document.getElementById(cmrpId('department','cb'));
    const courseSelect = document.getElementById(cmrpId('course','cb'));
    if (!departmentSelect || !courseSelect) return;

    const selectedDepartment = departmentSelect.value;
    courseSelect.innerHTML = '<option value="">Course</option>';

    // Clear child select (section) when department changes
    const sectionSelect = document.getElementById(cmrpId('section','cb'));
    if (sectionSelect) {
      sectionSelect.innerHTML = '<option value="">Section</option>';
      sectionSelect.disabled = true;
    }

    if (selectedDepartment) {
      const normalize = value =>
        String(value).toLowerCase().replace(/\s+/g, '');
      const filteredCourses = window.courseData.ddcs_course.filter(
        course => normalize(course.campus_entity_id) === normalize(this.campus_entity_id) && normalize(course.campus_department_id) === normalize(selectedDepartment)
      );

      if (filteredCourses.length > 0) {
        if(filteredCourses.length == 1){
          courseSelect.disabled = false;
          filteredCourses.forEach(course => {
            const option = document.createElement('option');
            option.value = course.course_id;
            cmrpStampOptionFullLabel(option, decodeHtmlEntities(course.coursenumber));
            option.selected = true;
            courseSelect.appendChild(option);
          });
          this.populateSections();
        }else{
          courseSelect.disabled = false;
          filteredCourses.forEach(course => {
            const option = document.createElement('option');
            option.value = course.course_id;
            cmrpStampOptionFullLabel(option, decodeHtmlEntities(course.coursenumber));
            courseSelect.appendChild(option);
          });
        }
        
      } else {
        this.disableSelects(['course', 'section']);
      }
    } else {
      this.disableSelects(['course', 'section']);
    }
    this.updateAddCourseButtonState();
  }

  async populateSections() {
    const courseSelect = document.getElementById(cmrpId('course','cb'));
    const sectionSelect = document.getElementById(cmrpId('section','cb'));
    if (!courseSelect || !sectionSelect) return;

    const selectedCourse = courseSelect.value;
    sectionSelect.innerHTML = '<option value="">Section</option>';

    if (selectedCourse) {
      const normalize = value =>
        String(value).toLowerCase().replace(/\s+/g, '');
      const filteredSections = window.courseData.ddcs_section.filter(
        section => normalize(section.campus_entity_id) === normalize(this.campus_entity_id) && normalize(section.term_id) === normalize(this.term_id) && normalize(section.course_id) === normalize(selectedCourse) 
      );
      
      if (filteredSections.length > 0) {
        if(filteredSections.length == 1){
          this.course_id = selectedCourse;
          sectionSelect.disabled = false;
          filteredSections.forEach(section => {
            const option = document.createElement('option');
            option.setAttribute('sectionGid', section.gid);
            const sectionLabel = decodeHtmlEntities(section.name);
            option.value = sectionLabel;
            cmrpStampOptionFullLabel(option, sectionLabel);
            option.selected = true;
            sectionSelect.appendChild(option);
          });
        }else{
          this.course_id = selectedCourse;
          sectionSelect.disabled = false;
          filteredSections.forEach(section => {
            const option = document.createElement('option');
            option.setAttribute('sectionGid', section.gid);
            const sectionLabel = decodeHtmlEntities(section.name);
            option.value = sectionLabel;
            cmrpStampOptionFullLabel(option, sectionLabel);
            sectionSelect.appendChild(option);
          });
        }
        
       
      } else {
        this.disableSelects(['section']);
      }
    } else {
      this.disableSelects(['section']);
    }

    // Load all courses when both campus and term are selected
    //
    this.updateAddCourseButtonState();
  }

  // Utility Methods
  disableSelects(selectIds) {
    selectIds.forEach(id => {
      const capitalizedId = id.charAt(0).toUpperCase() + id.slice(1);
      const placeholder = '<option value="">' + capitalizedId + '</option>';
      let nodes;
      if (id === 'campus') {
        const { cb, sid } = cmrpSuffixPair();
        const deskNodes = [];
        if (cb && sid && cmrpShowBothForms()) {
          const a = document.getElementById(cmrpId('campus', 'cb'));
          const b = document.getElementById(cmrpId('campus', 'sid'));
          if (a) deskNodes.push(a);
          if (b) deskNodes.push(b);
        } else {
          const desk = cmrpQueryDesktopCampusTerm('campus');
          if (desk) deskNodes.push(desk);
        }
        const mob = document.querySelector('#mobile-campus-term-dropdowns #campus');
        nodes = [...deskNodes, mob].filter(Boolean);
      } else if (id === 'term') {
        const { cb, sid } = cmrpSuffixPair();
        const deskNodes = [];
        if (cb && sid && cmrpShowBothForms()) {
          const a = document.getElementById(cmrpId('term', 'cb'));
          const b = document.getElementById(cmrpId('term', 'sid'));
          if (a) deskNodes.push(a);
          if (b) deskNodes.push(b);
        } else {
          const desk = cmrpQueryDesktopCampusTerm('term');
          if (desk) deskNodes.push(desk);
        }
        const mob = document.querySelector('#mobile-campus-term-dropdowns #term');
        nodes = [...deskNodes, mob].filter(Boolean);
      }
      if (nodes && nodes.length) {
        nodes.forEach(select => {
          select.innerHTML = placeholder;
          select.disabled = true;
        });
      } else {
        const scoped =
          id === 'division' ||
          id === 'department' ||
          id === 'course' ||
          id === 'section'
            ? cmrpId(id, 'cb')
            : id;
        const select = document.getElementById(scoped);
        if (select) {
          select.innerHTML = placeholder;
          select.disabled = true;
        }
      }
    });
  }

  setupDivisionField() {
    const divisionField = document.getElementById(cmrpId('division_field', 'cb'));
    const divisionSelect = document.getElementById(cmrpId('division', 'cb'));
    if (!divisionField || !divisionSelect) return;

    if (this.show_division) {
      divisionField.hidden = false;
      divisionField.style.display = 'block';
      divisionSelect.innerHTML = '<option value="">Division</option>';
      divisionSelect.disabled = true;
    } else {
      this.hideDivisionField();
    }
  }

  hideDivisionField() {
    const divisionField = document.getElementById(cmrpId('division_field', 'cb'));
    const divisionSelect = document.getElementById(cmrpId('division', 'cb'));
    if (!divisionField || !divisionSelect) return;

    divisionField.hidden = true;
    divisionField.style.display = 'none';
    divisionSelect.innerHTML = '<option value="">Division</option>';
    divisionSelect.disabled = true;
  }

  // Campus/Term Display Management
  showCampusTermDropdowns() {
    const { cb, sid } = cmrpSuffixPair();
    const rowScopes =
      cb && sid && cmrpShowBothForms() ? ['cb', 'sid'] : [null];
    const mobileDropdowns = document.getElementById('mobile-campus-term-dropdowns');
    if (mobileDropdowns) {
      mobileDropdowns.style.visibility = 'visible';
    }

    for (const rowScope of rowScopes) {
      const root = rowScope
        ? cmrpCampusTermRootId(rowScope)
        : cmrpCampusTermRootId('activeCt');
      const dropdowns = document.getElementById(root);
      const display = document.getElementById(
        cmrpId('campus-term-display', rowScope || 'activeCt'),
      );
      const campusSelect = rowScope
        ? document.getElementById(cmrpId('campus', rowScope))
        : cmrpQueryDesktopCampusTerm('campus');
      const termSelect = rowScope
        ? document.getElementById(cmrpId('term', rowScope))
        : cmrpQueryDesktopCampusTerm('term');

      if (dropdowns) {
        cmrpSetCampusTermDropdownContainerVisibility(dropdowns, 'show');
      }
      if (display) {
        display.style.display = 'none';
        display.classList.remove('show');
      }
      if (campusSelect) {
        campusSelect.disabled = false;
      }
      if (termSelect) {
        // Match populateTerms / completeCampusChangeForRow: term stays disabled until a campus is chosen.
        const hasCampus = Boolean(String(campusSelect?.value ?? '').trim());
        termSelect.disabled = !hasCampus;
      }
    }
    this.syncMobileDrawerCampusTermSelects();
  }

  showCampusTermDisplay(campusText, termText) {
    const root = cmrpCampusTermRootId('activeCt');
    const dropdowns = document.getElementById(root);
    const display = document.getElementById(cmrpId('campus-term-display', 'activeCt'));
    const textElement = document.getElementById(cmrpId('selected-campus-term-text', 'activeCt'));
    const campusTermDisplay = document.getElementById('selected-campus-display');
    // Hide dropdowns and show display when campus and term are selected
    if (dropdowns) {
      cmrpSetCampusTermDropdownContainerVisibility(dropdowns, 'hide');
    }
    if (display) {
      display.style.display = 'block';
      display.classList.add('show');
    }
    
    // Format display text based on available data
    let displayText = campusText;
    if (termText) {
      displayText = `${campusText} | ${termText}`;
    }
    
    if (textElement) textElement.textContent = displayText;
    
    // Also update the campus-term display for course section headers
    if (campusTermDisplay) {
      campusTermDisplay.textContent = displayText;
    }
  }

  async editCampusTerm() {
    // Always proceed with editing directly
    await this.proceedWithCampusTermEdit();
  }

  /**
   * Proceed with campus/term editing after confirmation (or if no confirmation needed)
   */
  async proceedWithCampusTermEdit() {
    // Set editing flag to prevent auto-switching back to display mode
    this.isEditingCampusTerm = true;

    // Store current campus and term values before switching to edit mode
    const cbSuf = cmrpSuffixPair().cb;
    const campusSelect = cbSuf
      ? document.getElementById(cmrpId('campus', 'cb'))
      : cmrpQueryDesktopCampusTerm('campus');
    const termSelect = cbSuf
      ? document.getElementById(cmrpId('term', 'cb'))
      : cmrpQueryDesktopCampusTerm('term');
    const currentCampus = campusSelect?.value || '';
    const currentTerm = termSelect?.value || '';

    // Show the dropdowns (this also enables them)
    this.showCampusTermDropdowns();

    // Disable Student ID mode to show course selection dropdowns
    this.disableStudentIdMode();

    // Ensure dropdowns are populated while preserving current selections
    this.populateCampus(true);

    // If we had a campus selected, populate terms and restore term selection
    if (currentCampus && campusSelect) {
      campusSelect.value = currentCampus;
      this.populateTermsPreservingSelection(currentTerm);
    }

    // Check if we need to enable course selection after edit
    if (campusSelect && termSelect && campusSelect.value && termSelect.value) {
      await this.enableCourseSelectionAfterEdit(termSelect);
    }
  }

  /**
   * Enable course selection dropdowns after editing campus/term
   */
  async enableCourseSelectionAfterEdit(termSelectEl) {
    await this.populateDepartmentsFromTerms(
      termSelectEl ||
        document.getElementById(cmrpId('term', 'cb')) ||
        cmrpQueryDesktopCampusTerm('term'),
    );
  }

  /** Edit campus/term for the student-id row only (does not switch global course-builder mode). */
  async proceedWithStudentIdCampusTermEdit() {
    this.isEditingCampusTerm = true;
    const campusSelect = document.getElementById(cmrpId('campus', 'sid'));
    const termSelect = document.getElementById(cmrpId('term', 'sid'));
    if (!campusSelect || !termSelect) return;
    const currentCampus = campusSelect.value || '';
    const currentTerm = termSelect.value || '';

    this.showCampusTermDropdowns();
    this.populateCampus(true);

    if (currentCampus) {
      campusSelect.value = currentCampus;
      this.populateTermsPreservingSelection(currentTerm, 'sid');
    }
  }

  /**
   * Apply `?campus=` / `?term=` once after campus/term lists are ready (not on every populateCampus).
   */
  _scheduleQueryCampusTermHydration() {
    const run = async () => {
      if (this._queryCampusTermHydratedOnce) return;
      this._queryCampusTermHydratedOnce = true;
      cmrpDdcsDebug('hydration.run', {
        fetchApplied: window.__cmrpCampusTermFetchApplied === true,
        search: String(window.location.search || '').slice(0, 200),
      });
      try {
        await this.handleURLParameters();
      } catch (e) {
        console.error('[CMRP] handleURLParameters failed', e);
      }
    };
    if (window.__cmrpCampusTermFetchApplied === true) {
      cmrpDdcsDebug('hydration.schedule', { mode: 'setTimeout(0)' });
      setTimeout(run, 0);
    } else {
      cmrpDdcsDebug('hydration.schedule', { mode: 'cmrp:campusTermDataReady' });
      document.addEventListener('cmrp:campusTermDataReady', run, { once: true });
    }
  }

  /**
   * Handle URL parameters for campus and term selection
   */
  async handleURLParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    const campusParam = urlParams.get('campus');
    const termParam = urlParams.get('term');

    cmrpDdcsDebug('handleURLParameters.start', { campusParam, termParam });

    if (!campusParam) {
      try {
        const p = urlParams;
        const deep =
          p.get('shopBy') ||
          p.get('termId') ||
          p.get('departmentDisplayName') ||
          p.get('courseDisplayName') ||
          p.get('sectionDisplayName') ||
          p.get('divisionDisplayName');
        cmrpDdcsDebug('handleURLParameters.skip', {
          reason:
            'no ?campus= query param (this path only hydrates human-readable campus/term).',
          hint: deep
            ? 'Deep links use shopBy, termId, departmentDisplayName, … — see url-state-manager attemptURLRestoration / restoreFieldsCascade.'
            : 'No deep-link course params either.',
          cmrp_flow: cmrpCurrentFlow(),
        });
      } catch (e) {
        cmrpDdcsDebug('handleURLParameters.skip', { reason: 'no campusParam' });
      }
      return;
    }

    const campusDecoded = decodeHtmlEntities(campusParam);
    const cbSuf = cmrpSuffixPair().cb;
    const campusSelect = cbSuf
      ? document.getElementById(cmrpId('campus', 'cb'))
      : this.getElementById('campus');
    if (!campusSelect) {
      cmrpDdcsDebug('handleURLParameters.abort', { reason: 'no campusSelect', cbSuf });
      return;
    }

    campusSelect.value = campusDecoded;
    cmrpDdcsDebug('handleURLParameters.campusSet', {
      campusSelectId: campusSelect.id,
      value: campusSelect.value,
    });
    await this.populateTerms(campusSelect);

    if (!termParam) {
      setTimeout(() => {
        this.showCampusTermDropdowns();
        const campusSelectRetry = cbSuf
          ? document.getElementById(cmrpId('campus', 'cb'))
          : this.getElementById('campus');
        if (campusSelectRetry) {
          campusSelectRetry.value = campusDecoded;
          this.completeCampusChange(campusDecoded);
        }
      }, 100);
      return;
    }

    const termSelect = cbSuf
      ? document.getElementById(cmrpId('term', 'cb'))
      : this.getElementById('term');
    if (!termSelect) {
      cmrpDdcsDebug('handleURLParameters.abort', { reason: 'no termSelect', cbSuf });
      return;
    }

    const tp = String(termParam).trim();
    const matchTermIndex = sel => {
      const opts = Array.from(sel.options);
      return opts.findIndex(
        opt =>
          opt.value &&
          (opt.value === tp ||
            decodeHtmlEntities(cmrpOptionFullLabel(opt)) === decodeHtmlEntities(tp) ||
            decodeHtmlEntities(cmrpOptionFullLabel(opt)).toLowerCase() ===
              decodeHtmlEntities(tp).toLowerCase()),
      );
    };

    let idx = matchTermIndex(termSelect);
    cmrpDdcsDebug('handleURLParameters.termMatch', {
      termParam: tp,
      termSelectId: termSelect.id,
      optionCount: termSelect.options.length,
      idxFirstPass: idx,
      optionSample: Array.from(termSelect.options)
        .slice(0, 6)
        .map(o => ({ value: o.value, text: o.textContent?.trim?.() })),
    });
    if (idx <= 0) {
      await new Promise(r => setTimeout(r, 400));
      const retry = cbSuf
        ? document.getElementById(cmrpId('term', 'cb'))
        : this.getElementById('term');
      if (retry) idx = matchTermIndex(retry);
      cmrpDdcsDebug('handleURLParameters.termMatch.retry', {
        idxAfterRetry: idx,
        optionCount: retry?.options?.length,
      });
    }

    const finalTerm =
      cbSuf
        ? document.getElementById(cmrpId('term', 'cb'))
        : this.getElementById('term');
    if (!finalTerm || idx <= 0) {
      const sample = finalTerm
        ? Array.from(finalTerm.options).map(o => ({
            value: o.value,
            text: o.textContent?.trim?.(),
          }))
        : [];
      console.warn('[CMRP DDCS] URL term did not match any <option> on course-builder term select.', {
        termParam: tp,
        termSelectId: finalTerm?.id,
        optionCount: sample.length,
        options: sample.slice(0, 12),
      });
      cmrpDdcsDebug('handleURLParameters.abort', {
        reason: '!finalTerm || idx <= 0',
        idx,
        hasFinalTerm: Boolean(finalTerm),
      });
      return;
    }

    const chosen = finalTerm.options[idx];
    finalTerm.selectedIndex = idx;
    if (chosen?.value) {
      finalTerm.value = chosen.value;
      this.term_id = chosen.value;
    }
    cmrpDdcsDebug('handleURLParameters.termChosen', {
      termSelectId: finalTerm.id,
      value: chosen?.value,
      text: chosen?.textContent?.trim?.(),
      this_term_id: this.term_id,
    });

    await this.populateDepartmentsFromTerms(finalTerm, {
      skipPageWideTermDeptReset: true,
    });
    cmrpDdcsDebug('handleURLParameters.afterPopulateDepartmentsFromTerms', {
      termSelectId: finalTerm.id,
      termValue: finalTerm.value,
    });
    this.showCampusTermDropdowns();
  }

  /**
   * Finish campus/term editing and return to normal mode
   */
  finishCampusTermEdit() {
    this.isEditingCampusTerm = false;
    // Re-sync campus/term chrome (single-campus student row hide, etc.) after leaving edit mode.
    this.populateCampus(true);
  }

  checkCampusTermSelection() {
    const cbSuf = cmrpSuffixPair().cb;
    const campusSelect = cbSuf
      ? document.getElementById(cmrpId('campus', 'cb'))
      : cmrpQueryDesktopCampusTerm('campus');
    const termSelect = cbSuf
      ? document.getElementById(cmrpId('term', 'cb'))
      : cmrpQueryDesktopCampusTerm('term');
    
    // Don't auto-switch to display mode if user is actively editing
    if (this.isEditingCampusTerm) {
      return false;
    }

    // Check if any courses have been added
    const hasCourses = this.config.selectedFilters && this.config.selectedFilters.length > 0;
    
    if (campusSelect?.value && termSelect?.value) {
      // Only show display mode if courses have been added
      if (hasCourses) {
        this.showCampusTermDropdowns();
        //this.showCampusTermDisplay(campusSelect.value, termSelect.options[termSelect.selectedIndex].text);
        return true;
      } else {
        // Keep dropdowns visible if no courses have been added
        this.showCampusTermDropdowns();
        return false;
      }
    } else {
      this.showCampusTermDropdowns();
      return false;
    }
  }

  // Validation
  validateRequiredFields() {
    const campus = cmrpCourseBuilderCampusTermEl('campus')?.value;
    const term = cmrpCourseBuilderCampusTermEl('term')?.value;
    const department = document.getElementById(cmrpId('department','cb'))?.value;
    const course = document.getElementById(cmrpId('course','cb'))?.value;
    const section = document.getElementById(cmrpId('section','cb'))?.value;

    return campus && term && department && course && section;
  }

  validateExistingFields() {
    var ret = true;
    const campusEl = cmrpCourseBuilderCampusTermEl('campus');
    const termEl = cmrpCourseBuilderCampusTermEl('term');
    const campus = campusEl?.value;
    const term = termEl?.value;
    const termText = termEl?.selectedOptions[0]?.text;
    const department = document.getElementById(cmrpId('department','cb'))?.value;
    const departmentText = document.getElementById(cmrpId('department','cb'))?.selectedOptions[0].text;
    const course = document.getElementById(cmrpId('course','cb'))?.value;
    const courseText = document.getElementById(cmrpId('course','cb'))?.selectedOptions[0].text;
    const section = document.getElementById(cmrpId('section','cb'))?.value;

    if (this.config.selectedFilters.length > 0) {
      for (const filter of this.config.selectedFilters) {
        if (
            filter.selectedDepartment === departmentText &&
            filter.selectedsection === section &&
            filter.selectedcourse === courseText &&
            filter.selectedTerm === termText &&
            filter.selectedTermId === term &&
            filter.selectedCampus === campus
        ) {
          ret = false; 
          break; 
        }
      }
    } 

    return ret
  }
  validateRequiredFieldsStudent() {
    const sidSuf = cmrpSuffixPair().sid;
    const campus = (
      sidSuf
        ? document.getElementById(cmrpId('campus', 'sid'))
        : typeof window.cmrpQueryDesktopCampusTerm === 'function'
          ? window.cmrpQueryDesktopCampusTerm('campus')
          : null
    )?.value;
    const term = (
      sidSuf
        ? document.getElementById(cmrpId('term', 'sid'))
        : typeof window.cmrpQueryDesktopCampusTerm === 'function'
          ? window.cmrpQueryDesktopCampusTerm('term')
          : null
    )?.value;
    const studentId = document.getElementById(cmrpId('student-id','sid'))?.value;
    const hasStudentId = String(studentId ?? '').trim().length > 0;

    return Boolean(campus && term && hasStudentId);
  }

  /**
   * Copy for student-ID submit validation: omit "Campus" when there is only one campus option
   * (matches populateCampus single-campus UX where the campus row is hidden/auto-selected).
   */
  getStudentIdRequiredFieldsMessage() {
    const filteredCampus = (this.config.ddcs_campus_entity || []).filter(
      campus => !campus.disabled_flag
    );
    const parts = [];
    if (filteredCampus.length > 1) {
      parts.push('Campus');
    }
    parts.push('Term');
    parts.push(cmrpStudentIdLabel());
    return `Please select all required fields: ${parts.join(', ')}.`;
  }

  updateAddCourseButtonState() {
    const addButton = document.getElementById(cmrpId('add-course','cb'));
    if (!addButton) return;

    // Keep Add Course always clickable so validation feedback appears on click.
    // `handleAddCourse()` is the source of truth for required-field warnings.
    addButton.disabled = false;
    addButton.classList.remove('disabled');
  }

  toggleAddCourseLoading(isLoading) {
    const button = document.getElementById(cmrpId('add-course','cb'));
    if (!button) return;

    const textEl = button.querySelector('.buy-by-course-builder__add-btn__text');

    if (isLoading) {
      if (!button.dataset.originalText) {
        // Never set `button.textContent` — it destroys the plus icon markup in `.buy-by-course-builder__add-btn__icon`.
        button.dataset.originalText = textEl
          ? (textEl.textContent || '').trim()
          : (button.textContent || '').trim();
      }
      button.disabled = true;
      if (textEl) {
        textEl.textContent = 'ADDING...';
      } else {
        button.textContent = 'ADDING...';
      }
    } else {
      if (button.dataset.originalText) {
        if (textEl) {
          textEl.textContent = button.dataset.originalText;
        } else {
          button.textContent = button.dataset.originalText;
        }
        delete button.dataset.originalText;
      } else if (textEl) {
        textEl.textContent = 'Add Course';
      } else {
        button.textContent = 'ADD COURSE';
      }
      this.updateAddCourseButtonState();
    }
  }
  async fetchProducts(queryData) {
    const proxyPath = '/apps/shopifyData/products';
    const url = `${proxyPath}?query=${queryData}`;

    try {
        const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'same-origin'
        });

        if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          return result.products;
        } else {
        throw new Error(result.error || 'Failed to fetch products');
        }

    } catch (error) {
        console.error('Error fetching products:', error);
        throw error;
    }
  }
  // Course Actions
  async handleAddCourse() {
    const courseBuilderWarningId = cmrpId('course-builder-error-warning', 'cb');
    if (typeof hideWarningMessage === 'function') {
      hideWarningMessage(courseBuilderWarningId);
    }

    if (!this.validateRequiredFields()) {
      if (
        window.courseMaterialsPageInstance &&
        typeof window.courseMaterialsPageInstance.showErrorMessage ===
          'function'
      ) {
        window.courseMaterialsPageInstance.showErrorMessage(
          'Please select all required fields: Campus, Term, Division, Department, Course, and Section.',
          '',
          false,
          { warningIds: [courseBuilderWarningId] }
        );
      } else {
        alert('Please select all required fields: Campus, Term, Division, Department, Course, and Section.');
      }
      return;
    }
    if (!this.validateExistingFields()) {
      if (
        window.courseMaterialsPageInstance &&
        typeof window.courseMaterialsPageInstance.showErrorMessage ===
          'function'
      ) {
        window.courseMaterialsPageInstance.showErrorMessage(
          'Your course selection is already being displayed.  Please enter new values.',
          '',
          false,
          { warningIds: [courseBuilderWarningId] }
        );
      } else {
        alert('Your course selection is already being displayed.  Please enter new values.');
      }
      return;

    }
    hideWarningMessage(cmrpId('student-id-error-warning', 'sid'));
    hideWarningMessage('mobile-student-id-error-warning');
    const selectElement = document.getElementById(cmrpId('section','cb'));

    this.toggleAddCourseLoading(true);

    try {
      hideWarningMessage(cmrpId('student-id-error-warning', 'sid'));
      const selectElement = document.getElementById(cmrpId('section','cb'));

      const chips = document.querySelectorAll('.course-chip');
      const chip_data =  Array.from(chips).map(chip => ({
        division: chip.getAttribute('data-division'),
        department: chip.getAttribute('data-department'),
        course: chip.getAttribute('data-course'),
        section: chip.getAttribute('data-section'),
        key: chip.getAttribute('data-course-key')
      }));
      const courseData = this.getCourseFormData();
      const objCourse = this.createCourseObject(courseData);
      
      // Add course to selected courses display
      const divisionEl = document.getElementById(cmrpId('division', 'cb'));
      const divisionLabel = cmrpSelectSelectedFullLabel(divisionEl);
      let division =
        divisionLabel && divisionLabel !== 'Division' ? divisionLabel : '';

      const departmentEl = document.getElementById(cmrpId('department', 'cb'));
      const departmentLabel = cmrpSelectSelectedFullLabel(departmentEl);
      let department =
        departmentLabel && departmentLabel !== 'Department'
          ? departmentLabel
          : '';

      const courseEl = document.getElementById(cmrpId('course', 'cb'));
      const courseLabel = cmrpSelectSelectedFullLabel(courseEl);
      let course =
        courseLabel && courseLabel !== 'Course' ? courseLabel : '';

      const sectionSelect = document.getElementById(cmrpId('section', 'cb'));
      const sectionValue = sectionSelect?.value?.trim() || '';
      const sectionTextRaw = sectionSelect
        ? cmrpSelectSelectedFullLabel(sectionSelect)
        : '';
      const sectionText =
        sectionTextRaw && sectionTextRaw !== 'Section' && sectionTextRaw.trim() !== ''
          ? sectionTextRaw.trim()
          : '';
      if (department && course && sectionValue) {
        // Use option value for identity (matches selectedFilters + syncBubblesFromFilters); label can stay human-readable.
        this.addSelectedCourse(department, course, sectionValue, sectionText || sectionValue);
      }

      document.dispatchEvent(
        new CustomEvent('sidebar:courseAdded', {
          detail: { course: objCourse },
        })
      );

      // Finish campus/term editing since user has made a selection
      this.finishCampusTermEdit();

      await this.resetCourseBuilderLineKeepCampusTerm();

      this.resetCmrpStudentRowAfterCourseBuilderSubmit();

      if (window.innerWidth <= 968 && !cmrpIsCourseMaterialsResultsPage()) {
        window.courseMaterialsSidebar?.closeMobileDrawer?.();
      }
    } finally {
      this.toggleAddCourseLoading(false);
    }
}

async handleAddStudent() {
  if (!this.validateRequiredFieldsStudent()) {
    // Use the page's error display method
    if (
      window.courseMaterialsPageInstance &&
      typeof window.courseMaterialsPageInstance.showErrorMessage ===
        'function'
    ) {
      window.courseMaterialsPageInstance.showErrorMessage(
        `Please select all required fields: Campus, Term, ${cmrpStudentIdLabel()}.`
      );
    } else {
      alert(`Please select all required fields: Campus, Term, ${cmrpStudentIdLabel()}.`);
    }
    return;
  }

  this.toggleSubmitLoading(true);

  const studentData = this.getStudentFormData();

  document.dispatchEvent(
    new CustomEvent('sidebar:studentSubmitted', {
      detail: {
        studentData,
        onComplete: async () => {
          this.toggleSubmitLoading(false);
          // Finish campus/term editing since user has made a selection
          this.finishCampusTermEdit();
        },
        onError: () => this.toggleSubmitLoading(false),
      },
    })
  );
}

  getCourseFormData() {
    const cbSuf = cmrpSuffixPair().cb;
    const campusForCourse = cbSuf
      ? document.getElementById(cmrpId('campus', 'cb'))
      : cmrpQueryDesktopCampusTerm('campus');
    const termForCourse = cbSuf
      ? document.getElementById(cmrpId('term', 'cb'))
      : cmrpQueryDesktopCampusTerm('term');

    const selectElement = document.getElementById(cmrpId('section', 'cb'));
    if (!selectElement) {
      return {
        selectedDepartment: '',
        selectedCampus: campusForCourse?.value || '',
        selectedTerm: '',
        selectedcourse: '',
        selectedsection: '',
        selectedsectionGid: '',
        selectedDivision: '',
      };
    }

    const optionToFind = Array.from(selectElement.options).find(
      option => option.value === selectElement.value,
    );
    const sectionGid = optionToFind?.getAttribute('sectiongid');

    let department = cmrpSelectSelectedFullLabel(
      document.getElementById(cmrpId('department', 'cb')),
    );

    let division = cmrpSelectSelectedFullLabel(
      document.getElementById(cmrpId('division', 'cb')),
    );

    let term = cmrpSelectSelectedFullLabel(termForCourse);

    let course = cmrpSelectSelectedFullLabel(
      document.getElementById(cmrpId('course', 'cb')),
    );

    return {
      selectedDepartment: department != 'Department' && department != '' ? department : '',
      selectedCampus: campusForCourse?.value || '',
      selectedTerm: term != 'Term' && term != '' ? term : '',
      selectedcourse: course != 'Course' && course != '' ? course : '',
      selectedsection: document.getElementById(cmrpId('section','cb'))?.value || '',
      selectedsectionGid: sectionGid || '',
      selectedDivision: division != 'Division' && division != '' ? division : '',
    };
  }

  getStudentFormData() {
    const sidSuf = cmrpSuffixPair().sid;
    const campusSid = sidSuf
      ? document.getElementById(cmrpId('campus', 'sid'))
      : cmrpQueryDesktopCampusTerm('campus');
    const termSid = sidSuf
      ? document.getElementById(cmrpId('term', 'sid'))
      : cmrpQueryDesktopCampusTerm('term');
    return {
      selectedCampus: campusSid?.value || '',
      selectedTerm: termSid?.value || '',
      studentId: document.getElementById(cmrpId('student-id','sid'))?.value || '',
    };
  }

  createCourseObject(courseData) {
    let selectedTermId = '';
    if (courseData.selectedTerm) {
      const normalize = value =>
        String(value).toLowerCase().replace(/\s+/g, '');
      const filteredTerm = window.courseData.ddcs_term.filter(term =>
        normalizeDdcsLabel(term.name).includes(normalizeDdcsLabel(courseData.selectedTerm))
      );
      if (filteredTerm.length > 0) {
        selectedTermId = filteredTerm[0].term_id;
      }
    }

    return {
      selectedDepartment: courseData.selectedDepartment,
      selectedCampus: courseData.selectedCampus,
      selectedTerm: courseData.selectedTerm,
      selectedTermId: selectedTermId,
      selectedcourse: courseData.selectedcourse,
      selectedsection: courseData.selectedsection,
      selectedDivision: courseData.selectedDivision,
      selectedsectionGid: courseData.selectedsectionGid
    };
  }

  toggleSubmitLoading(isLoading) {
    const button = document.getElementById(cmrpId('submit-student-id','sid'));
    if (!button) return;

    const btnText = button.querySelector('.btn-text');
    const btnSpinner = button.querySelector('.btn-spinner');

    if (isLoading) {
      button.disabled = true;
      if (btnText) btnText.textContent = 'SUBMITTING...';
      if (btnSpinner) btnSpinner.style.display = 'inline-block';
    } else {
      button.disabled = false;
      if (btnText) btnText.textContent = 'SUBMIT';
      if (btnSpinner) btnSpinner.style.display = 'none';
    }
  }

  // Warning Management
  showCampusChangeWarning() {
    if (typeof showWarningMessage === 'function') {
      showWarningMessage(cmrpId('campus-change-warning', 'activeCt'));
    }
  }

  hideCampusChangeWarning() {
    if (typeof hideWarningMessage === 'function') {
      hideWarningMessage(cmrpId('campus-change-warning', 'activeCt'));
    }
  }

  /**
   * Handle campus change confirmation - user chose to continue
   * @param {string} warningId - The warning message ID
   */
  handleCampusChangeAccept(warningId) {
    // Hide the warning message
    this.hideCampusChangeWarning();

    // Proceed with the campus change (this will clear selections)
    if (this.pendingCampusChange && this.pendingCampusChange.selectedCampus) {
      this.completeCampusChange(this.pendingCampusChange.selectedCampus);
      this.pendingCampusChange = null;
    }
  }

  /**
   * Handle campus change cancellation - user chose to cancel
   * @param {string} warningId - The warning message ID
   */
  handleCampusChangeCancel(warningId) {
    // Hide the warning message
    this.hideCampusChangeWarning();

    // Cancel the pending campus change, keep selections intact
    this.pendingCampusChange = null;

    // Campus dropdown is already reset to previous value in populateTerms()
    // No further action needed - selections remain intact
  }

  // State Management
  clearAllUserSelections(clearCampus = false, clearTerm = false) {
    document.dispatchEvent(
      new CustomEvent('sidebar:clearSelections', {
        detail: { clearCampus, clearTerm },
      })
    );

    if (clearTerm) {
      const termSelect = cmrpCourseBuilderCampusTermEl('term');
      if (termSelect) {
        termSelect.innerHTML = '<option value="">Term</option>';
        termSelect.disabled = true;
      }
    }

    this.clearDDCSSelections();
  }
  clearAllTermSelections(clearTerm = false) {
    document.dispatchEvent(
      new CustomEvent('sidebar:clearAllTermSelections', {
        detail: { clearTerm },
      })
    );

    this.clearDDCSSelections();
  }

  clearDDCSSelections(enableFirstAvailable = false) {
    this.campus_department_id = '';
    this.course_id = '';
    this.division_id = '';

    const courseSelect = document.getElementById(cmrpId('course','cb'));
    const sectionSelect = document.getElementById(cmrpId('section','cb'));

    if (courseSelect) {
      courseSelect.innerHTML = '<option value="">Course</option>';
      courseSelect.disabled = true;
    }
    if (sectionSelect) {
      sectionSelect.innerHTML = '<option value="">Section</option>';
      sectionSelect.disabled = true;
    }

    const shouldShowDivision = this.show_division;
    const divisionSelect = document.getElementById(cmrpId('division','cb'));
    const departmentSelect = document.getElementById(cmrpId('department','cb'));

    if (enableFirstAvailable && shouldShowDivision) {
      if (divisionSelect) {
        divisionSelect.innerHTML = '<option value="">Division</option>';
        this.populateDivision();
      }
      if (departmentSelect) {
        departmentSelect.innerHTML = '<option value="">Department</option>';
        departmentSelect.disabled = true;
      }
    } else if (enableFirstAvailable && !shouldShowDivision) {
      if (divisionSelect) {
        divisionSelect.innerHTML = '<option value="">Division</option>';
        divisionSelect.disabled = true;
      }
      if (departmentSelect) {
        departmentSelect.innerHTML = '<option value="">Department</option>';
        this.populateDepartments();
      }
    } else {
      if (divisionSelect) {
        divisionSelect.innerHTML = '<option value="">Division</option>';
        divisionSelect.disabled = true;
      }
      if (departmentSelect) {
        departmentSelect.innerHTML = '<option value="">Department</option>';
        departmentSelect.disabled = true;
      }
    }

    this.updateAddCourseButtonState();
  }

  /**
   * Clear division→section for the course-builder row while keeping campus + term as-is,
   * then re-enable the first DDCS step (division or department) for the current term.
   */
  async resetCourseBuilderLineKeepCampusTerm() {
    const campusSelect = cmrpCourseBuilderCampusTermEl('campus');
    const termSelect = cmrpCourseBuilderCampusTermEl('term');
    const campusName = campusSelect?.value;
    const selectedTerm = termSelect?.value;

    const normalize = value =>
      String(value ?? '')
        .toLowerCase()
        .replace(/\s+/g, '');
    const campusPool =
      (Array.isArray(this.config.ddcs_campus_entity) &&
        this.config.ddcs_campus_entity.length > 0 &&
        this.config.ddcs_campus_entity) ||
      window.courseData?.ddcs_campus_entity ||
      [];

    if (campusName && campusPool.length) {
      const filteredCampus = campusPool.filter(campus =>
        normalizeDdcsLabel(campus.name).includes(normalizeDdcsLabel(campusName)),
      );
      if (filteredCampus.length > 0) {
        this.campus_entity_id = filteredCampus[0].campus_entity_id;
        this.show_division = filteredCampus[0].is_division_used_flag;
      }
    }
    if (selectedTerm) {
      this.term_id = selectedTerm;
    }

    this.setupDivisionField();
    this.clearDDCSSelections(false);

    if (selectedTerm && this.campus_entity_id) {
      window.courseData.ddcs_campus_division = [];
      window.courseData.ddcs_campus_department = [];
      window.courseData.ddcs_course = [];
      window.courseData.ddcs_section = [];
      try {
        const dataFilters = await this.fetchMetaobjectsData(
          this.term_id,
          this.campus_entity_id,
        );
        window.courseData.ddcs_campus_division = dataFilters.ddcs_campus_division;
        window.courseData.ddcs_campus_department =
          dataFilters.ddcs_campus_department;
        window.courseData.ddcs_course = dataFilters.ddcs_course;
        window.courseData.ddcs_section = dataFilters.ddcs_section;
        this.setupDivisionField();
        if (this.show_division) {
          await this.populateDivision();
        } else {
          await this.populateDepartments();
        }
      } catch (e) {
        console.error(
          '[CMRP] resetCourseBuilderLineKeepCampusTerm refetch failed',
          e,
        );
      }
    }

    this.checkCampusTermSelection();
    this.updateAddCourseButtonState();
  }

  // Clear all course fields after adding a course to allow easy addition of next course
  clearSectionForNextAddition() {
    const divisionSelect = document.getElementById(cmrpId('division','cb'));
    const departmentSelect = document.getElementById(cmrpId('department','cb'));
    const courseSelect = document.getElementById(cmrpId('course','cb'));
    const sectionSelect = document.getElementById(cmrpId('section','cb'));

    if (divisionSelect) {
      divisionSelect.value = '';
    }
    if (departmentSelect) {
      departmentSelect.value = '';
    }
    if (courseSelect) {
      courseSelect.value = '';
    }
    if (sectionSelect) {
      sectionSelect.value = '';
    }

    // Re-enable the dropdowns cascade for next course selection
    if (this.show_division) {
      this.populateDivision();
    } else {
      this.populateDepartments();
    }

    this.updateAddCourseButtonState();
  }

  /**
   * When both CMRP forms are visible: clear the student-ID form so it looks untouched
   * after a successful shop-by-course submission.
   */
  resetCmrpStudentRowAfterCourseBuilderSubmit() {
    if (!cmrpShowBothForms()) return;

    this.isEditingCampusTerm = false;

    const studentInput = document.getElementById(cmrpId('student-id', 'sid'));
    if (studentInput) studentInput.value = '';
    try {
      sessionStorage.removeItem(CMRP_STUDENT_ID_PREFILL_KEY);
    } catch (e) {
      /* ignore */
    }
    if (typeof hideWarningMessage === 'function') {
      hideWarningMessage(cmrpId('student-id-error-warning', 'sid'));
      hideWarningMessage('mobile-student-id-error-warning');
    }

    const termSid = document.getElementById(cmrpId('term', 'sid'));
    if (termSid) {
      termSid.innerHTML = '<option value="">Term</option>';
      termSid.disabled = true;
    }
    const campusSid = document.getElementById(cmrpId('campus', 'sid'));
    if (campusSid) {
      campusSid.value = '';
    }

    this.populateCampus(false, 'sid');
    this.syncMobileDrawerCampusTermSelects();
    window.applyCmrpCourseDetailsSectionFlowUi?.();
    anchorCmrpProductsPanel();
  }

  /**
   * When both CMRP forms are visible: clear the course-builder row so it looks untouched
   * after a student-ID submission (page clears filters/grid separately).
   */
  resetCmrpCourseBuilderRowAfterStudentSubmit() {
    if (!cmrpShowBothForms()) return;

    this.isEditingCampusTerm = false;
    this.allCoursesData = [];
    this.hideAllCoursesList();

    this.clearDDCSSelections(false);

    const termCb = document.getElementById(cmrpId('term', 'cb'));
    if (termCb) {
      termCb.innerHTML = '<option value="">Term</option>';
      termCb.disabled = true;
    }
    const campusCb = document.getElementById(cmrpId('campus', 'cb'));
    if (campusCb) {
      campusCb.value = '';
    }

    const chipBox = document.getElementById(cmrpId('selected-courses-container', 'cb'));
    if (chipBox) chipBox.innerHTML = '';

    this.populateCampus(false, 'cb');
    this.syncMobileDrawerCampusTermSelects();
    this.updateAddCourseButtonState();
    window.applyCmrpCourseDetailsSectionFlowUi?.();
    anchorCmrpProductsPanel();
  }

  /**
   * Enable Student ID mode - hide course selection dropdowns
   */
  enableStudentIdMode() {
    cmrpPersistFlow('student-id');
    const filtersPanel = document.getElementById('filters-section');
    if (filtersPanel) {
      filtersPanel.classList.add('student-id-mode');
    }

    // Hide course selection fields when in Student ID mode
    this.hideCourseSelectionFields();
    window.applyCmrpCourseDetailsSectionFlowUi?.();
    anchorCmrpProductsPanel();
  }

  /**
   * Disable Student ID mode - show course selection dropdowns
   * @param {boolean} [preserveStudentIdInput=false] - When true, keep desktop/mobile student ID fields (e.g. mobile drawer tab switch before submit)
   */
  disableStudentIdMode(preserveStudentIdInput = false) {
    cmrpPersistFlow('course-builder');
    const filtersPanel = document.getElementById('filters-section');
    if (filtersPanel) {
      filtersPanel.classList.remove('student-id-mode');
    }

    if (!preserveStudentIdInput) {
      const studentIdInput = document.getElementById(cmrpId('student-id','sid'));
      if (studentIdInput) {
        studentIdInput.value = '';
      }
      const mobileStudentIdInput = document.getElementById('mobile-student-id-input');
      if (mobileStudentIdInput) {
        mobileStudentIdInput.value = '';
      }
    }

    // Show course selection fields when disabling Student ID mode
    this.showCourseSelectionFields();
    window.applyCmrpCourseDetailsSectionFlowUi?.();
    anchorCmrpProductsPanel();
  }

  /**
   * Switch to manual course selection mode.
   * Persists course-builder flow in sessionStorage and refreshes without cmrp_flow in URL.
   */
  switchToManualCourseSelection() {
    cmrpPersistFlow('course-builder');
    const q = new URLSearchParams(window.location.search);
    q.delete(CMRP_FLOW_QUERY_PARAM);
    const qs = q.toString();
    window.location.href = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  }

  hideCourseSelectionFields() {
    if (cmrpShowBothForms()) {
      return;
    }
    cmrpCourseBuilderCourseLineFieldEls().forEach(el => {
      el.style.display = 'none';
    });

    // Also hide the "What's the course name?" title if it exists separately
    const courseTitles = document.querySelectorAll('h3, .section-title');
    courseTitles.forEach(title => {
      if (
        title.textContent &&
        title.textContent.includes("What's the course name")
      ) {
        title.style.display = 'none';
      }
    });
  }

  showCourseSelectionFields() {
    if (cmrpShowBothForms()) {
      return;
    }
    cmrpCourseBuilderCourseLineFieldEls().forEach(el => {
      el.style.display = '';
    });

    // Show the "What's the course name?" title
    const courseTitles = document.querySelectorAll('h3, .section-title');
    courseTitles.forEach(title => {
      if (
        title.textContent &&
        title.textContent.includes("What's the course name")
      ) {
        title.style.display = '';
      }
    });
  }

  // All Courses List Management
  initializeAllCoursesList() {
    this.selectedCoursesFromList = new Set();
    this.allCoursesData = [];
  }

  /**
   * Initialize mobile trigger button visibility
   */
  initializeMobileTrigger() {
    const mobileTrigger = document.getElementById('mobile-course-trigger');
    const drawer = document.getElementById('mobile-drawer-section');
    const backdrop = document.getElementById('mobile-drawer-backdrop');

    if (!drawer || !backdrop) return;

    if (mobileTrigger && drawer && backdrop) {
      // Check if we're on mobile
      const isMobile = window.innerWidth <= 480;
      
      if (isMobile) {
        mobileTrigger.style.display = 'block';
        drawer.style.display = 'none';
        // Don't force backdrop to display: none, let CSS handle it
      } else {
        mobileTrigger.style.display = 'none';
        drawer.style.display = 'none';
        backdrop.style.display = 'none';
      }
    }
  }

  loadAllCourses() {
    const campusSelect = cmrpCourseBuilderCampusTermEl('campus');
    const termSelect = cmrpCourseBuilderCampusTermEl('term');
    const divisionSelect = document.getElementById(cmrpId('division','cb'));
    const departmentSelect = document.getElementById(cmrpId('department','cb'));
    const courseSelect = document.getElementById(cmrpId('course','cb'));
    
    if (!campusSelect?.value || !termSelect?.value || !departmentSelect?.value || !courseSelect?.value) {
      this.hideAllCoursesList();
      return;
    }

    const isNarrowViewport = window.innerWidth <= 968;
    const useMobileAllCoursesList =
      isNarrowViewport && !cmrpIsCourseMaterialsResultsPage();

    this.showCoursesLoading();

    try {
      if (useMobileAllCoursesList) {
        this.loadAllCoursesForMobile();
      } else {
        // Desktop: use existing data and existing logic
        //this.allCoursesData = this.getAllAvailableCourses();
        //this.renderAllCoursesList();
        //this.hideCoursesLoading();
      }

    } catch (error) {
      console.error('Error loading all courses:', error);
      this.hideAllCoursesList();
      this.hideCoursesLoading();
    }
  }

  async loadAllCoursesForMobile() {
    const campusSelect = cmrpCourseBuilderCampusTermEl('campus');
    const termSelect = cmrpCourseBuilderCampusTermEl('term');
    const divisionSelect = document.getElementById(cmrpId('division','cb'));
    const departmentSelect = document.getElementById(cmrpId('department','cb'));
    const courseSelect = document.getElementById(cmrpId('course','cb'));
    
    if (!campusSelect?.value || !termSelect?.value || !departmentSelect?.value || !courseSelect?.value) {
      this.hideCoursesLoading();
      return;
    }

    try {
      // Find campus and term IDs
      const campus = this.config.ddcs_campus_entity.find(c => c.name === campusSelect.value);
      const term = window.courseData.ddcs_term.find(t => t.name === termSelect.value && t.campus_entity_id === campus?.campus_entity_id);
      
      if (!campus || !term) {
        this.hideCoursesLoading();
        return;
      }

      // Fetch all departments for this campus
      const deptQueryData = `fields.campus_entity_id:'${campus.campus_entity_id}' fields.disabled_flag:false`;
      const departments = await this.fetchMetaobjects('ddcs_department', deptQueryData);
      const sortValuesByName = (a, b) => {
        const nameA = a.name;
        const nameB = b.name;

        const isNumA = !isNaN(nameA.charAt(0));
        const isNumB = !isNaN(nameB.charAt(0));
        
        if (isNumA && isNumB) {
          return parseInt(nameA) - parseInt(nameB); 
        }
        
        if (isNumA) {
          return -1; 
        }
        
        if (isNumB) {
          return 1; 
        }
        
        return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
      };
      const sortedValues = departments.sort(sortValuesByName);
      this.allCoursesData = [];

      window.courseData.ddcs_section.forEach(section => {
        const course = window.courseData.ddcs_course.find(c => c.course_id === section.course_id && c.campus_entity_id === section?.campus_entity_id);
        const department = window.courseData.ddcs_campus_department.find(d => d.campus_department_id === course?.campus_department_id && d.campus_entity_id === section?.campus_entity_id);
        if (!course || !department) return;
        this.allCoursesData.push({
          id: `${section.course_id}_${section.section_id}`,
          course_code: course.coursenumber,
          course: course.coursenumber,
          section: section.name,
          section_number: section.name,
          department: department.name,
          division: department.division_name || '',
          course_id: section.course_id,
          section_id: section.section_id,
          department_id: department.campus_department_id
        });
      });
      

      // Render the courses list for mobile
      this.renderAllCoursesList('');
      this.hideCoursesLoading();


    } catch (error) {
      console.error('Error loading courses for mobile:', error);
      this.hideCoursesLoading();
    }
  }

  getAllAvailableCourses() {
    const allCourses = [];
    
    // Get current campus and term IDs
    const campusName = cmrpCourseBuilderCampusTermEl('campus')?.value;
    const termName = cmrpCourseBuilderCampusTermEl('term')?.value;
    
    if (!campusName || !termName) return [];
    
    // Find campus and term IDs
    const normalize = value =>
      String(value).toLowerCase().replace(/\s+/g, '');
    const campus = this.config.ddcs_campus_entity.find(
      c => normalizeDdcsLabel(c.name) === normalizeDdcsLabel(campusName),
    );
    const term = this.config.ddcs_term.find(
      t =>
        normalize(t.term_id) === normalize(termName) &&
        normalize(t.campus_entity_id) === normalize(campus?.campus_entity_id),
    );
    
    if (!campus || !term) return [];
    
    // Get all departments for this campus
    const departments = this.config.ddcs_campus_department.filter(dept => 
      normalize(dept.campus_entity_id) === normalize(campus.campus_entity_id)
    );
    
    // Get all courses and sections for each department
    departments.forEach(department => {
      const courses = this.config.ddcs_course.filter(course => 
        normalize(course.campus_entity_id) === normalize(campus.campus_entity_id) &&
        normalize(course.campus_department_id) === normalize(department.campus_department_id)
      );
      
      courses.forEach(course => {
        const sections = this.config.ddcs_section.filter(section =>
          normalize(section.campus_entity_id) === normalize(campus.campus_entity_id) &&
          normalize(section.term_id) === normalize(term.term_id) &&
          normalize(section.course_id) === normalize(course.course_id)
        );
        
        sections.forEach(section => {
          allCourses.push({
            id: `${course.course_id}_${section.section_id}`,
            course_code: course.coursenumber,
            course: course.coursenumber,
            section: section.name,
            section_number: section.name,
            department: department.name,
            division: department.division_name || '',
            course_id: course.course_id,
            section_id: section.section_id,
            department_id: department.campus_department_id
          });
        });
      });
    });
    
    return allCourses;
  }

  renderAllCoursesList() {
    const coursesList = document.getElementById('all-courses-list');
    const coursesContainer = coursesList?.querySelector('.courses-container');
    
    if (!coursesList || !coursesContainer) return;

    if (this.allCoursesData.length === 0) {
      // Show loading state for a bit longer before showing "no courses" message
      setTimeout(() => {
        coursesContainer.innerHTML = '<div class="no-courses-message">No courses available for this campus and term.</div>';
        coursesList.style.display = 'block';
      }, 1500); // Wait 1.5 seconds before showing the message
      return;
    }

    coursesContainer.innerHTML = '';

    this.allCoursesData.forEach(course => {
      const courseItem = document.createElement('div');
      courseItem.className = `course-item ${this.selectedCoursesFromList.has(course.id) ? 'selected' : ''}`;
      courseItem.dataset.courseId = course.id;
      
      courseItem.innerHTML = `
        <span class="course-item-text">${course.course_code || course.course}, SECTION ${course.section || course.section_number}</span>
        <div class="course-item-action">
          ${this.selectedCoursesFromList.has(course.id) ? 
            '<svg class="check-icon" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" fill="currentColor"/></svg>' :
            '<svg class="plus-icon" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3.333V12.667M3.333 8H12.667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          }
        </div>
      `;
      
      courseItem.addEventListener('click', () => this.toggleCourseFromList(course));
      coursesContainer.appendChild(courseItem);
    });

    coursesList.style.display = 'block';
  }

  toggleCourseFromList(course) {
    const courseItem = document.querySelector(`[data-course-id="${course.id}"]`);
    if (!courseItem) return;

    const isSelected = this.selectedCoursesFromList.has(course.id);
    const actionElement = courseItem.querySelector('.course-item-action');
    
    if (isSelected) {
      // Remove the course
      this.selectedCoursesFromList.delete(course.id);
      courseItem.classList.remove('selected');
      
      // Update icon to plus
      if (actionElement) {
        actionElement.innerHTML = '<svg class="plus-icon" width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M8 3.333V12.667M3.333 8H12.667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      }
      
      // Remove from the form
      this.removeCourseFromList(course);
    } else {
      // Add the course
      this.selectedCoursesFromList.add(course.id);
      courseItem.classList.add('selected');
      
      // Update icon to check
      if (actionElement) {
        actionElement.innerHTML = '<svg class="check-icon" width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" fill="currentColor"/></svg>';
      }
      
      // Add to the form
      this.addCourseFromList(course);
    }
  }

  addCourseFromList(course) {
    // Create course object similar to the dropdown selection
    const courseData = {
      selectedDepartment: course.department || '',
      selectedCampus: cmrpCourseBuilderCampusTermEl('campus')?.value || '',
      selectedTerm: cmrpCourseBuilderCampusTermEl('term')?.value || '',
      selectedcourse: course.course_code || course.course || '',
      selectedsection: course.section || course.section_number || '',
      selectedDivision: course.division || '',
    };

    const objCourse = this.createCourseObject(courseData);

    // Dispatch the same event as the dropdown "Add course" button
    document.dispatchEvent(
      new CustomEvent('sidebar:courseAdded', {
        detail: { course: objCourse },
      })
    );

  }

  removeCourseFromList(course) {
    // Create course object similar to the dropdown selection
    const courseData = {
      selectedDepartment: course.department || '',
      selectedCampus: cmrpCourseBuilderCampusTermEl('campus')?.value || '',
      selectedTerm: cmrpCourseBuilderCampusTermEl('term')?.value || '',
      selectedcourse: course.course_code || course.course || '',
      selectedsection: course.section || course.section_number || '',
      selectedDivision: course.division || '',
    };

    const objCourse = this.createCourseObject(courseData);

    const cmPage =
      window.courseMaterialsPage || window.courseMaterialsPageInstance;
    cmPage?.removeCourseSelection?.(
      objCourse.selectedDepartment,
      objCourse.selectedcourse,
      objCourse.selectedsection
    );
  }

  showCoursesLoading() {
    // Try desktop first, then mobile
    let loading = document.getElementById('courses-loading');
    let coursesList = document.getElementById('all-courses-list');
    
    
    if (loading) loading.style.display = 'flex';
    if (coursesList) coursesList.style.display = 'none';
  }

  hideCoursesLoading() {
    // Try desktop first
    let loading = document.getElementById('courses-loading');
    if (loading) loading.style.display = 'none';
  }

  hideAllCoursesList() {
    // Try desktop first
    let coursesList = document.getElementById('all-courses-list');
    if (coursesList) coursesList.style.display = 'none';
  }

  // Course filtering functionality
  filterCourses(event) {
    const searchTerm = event.target.value.toLowerCase().trim();
    this.renderAllCoursesList(searchTerm);
  }

  renderAllCoursesList(searchTerm = '') {
    // Try desktop first, then mobile
    let coursesList = document.getElementById('all-courses-list');
    let coursesContainer = coursesList?.querySelector('.courses-container');
    
    if (!coursesList || !coursesContainer) return;

    if (this.allCoursesData.length === 0) {
      // Show loading state for a bit longer before showing "no courses" message
      setTimeout(() => {
        const messageClass = 'no-courses-message';
        coursesContainer.innerHTML = `<div class="${messageClass}">No courses available for this campus and term.</div>`;
        coursesList.style.display = 'block';
      }, 1500); // Wait 1.5 seconds before showing the message
      return;
    }

    // Filter courses based on search term
    let filteredCourses = this.allCoursesData;
    if (searchTerm) {
      filteredCourses = this.allCoursesData.filter(course => {
        const courseCode = (course.course_code || course.course || '').toLowerCase();
        const section = (course.section || course.section_number || '').toLowerCase();
        return courseCode.includes(searchTerm) || section.includes(searchTerm);
      });
    }

    if (filteredCourses.length === 0) {
      const messageClass = isMobile ? 'mobile-no-courses-message' : 'no-courses-message';
      coursesContainer.innerHTML = `<div class="${messageClass}">No courses found matching your search.</div>`;
      coursesList.style.display = 'block';
      return;
    }

    coursesContainer.innerHTML = '';

    filteredCourses.forEach(course => {
      const courseItem = document.createElement('div');
      const itemClass = isMobile ? 'mobile-course-item' : 'course-item';
      const textClass = isMobile ? 'mobile-course-item-text' : 'course-item-text';
      courseItem.className = `${itemClass} ${this.selectedCoursesFromList.has(course.id) ? 'selected' : ''}`;
      courseItem.dataset.courseId = course.id;
      
      if (isMobile) {
        // Mobile version - simpler layout without action icons
        courseItem.innerHTML = `
          <span class="${textClass}">${course.course_code || course.course}, SECTION ${course.section || course.section_number}</span>
        `;
      } else {
        // Desktop version - with action icons
        courseItem.innerHTML = `
          <span class="${textClass}">${course.course_code || course.course}, SECTION ${course.section || course.section_number}</span>
          <div class="course-item-action">
            ${this.selectedCoursesFromList.has(course.id) ? 
              '<svg class="check-icon" width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" fill="currentColor"/></svg>' :
              '<svg class="plus-icon" width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M8 3.333V12.667M3.333 8H12.667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
            }
          </div>
        `;
      }
      
      courseItem.addEventListener('click', () => this.toggleCourseFromList(course));
      coursesContainer.appendChild(courseItem);
    });

    coursesList.style.display = 'block';
  }

  /**
   * Toggle mobile drawer visibility
   */
  toggleMobileDrawer() {
    const drawer = document.getElementById('mobile-drawer-section');
    if (!drawer) return;

    const isVisible = drawer.classList.contains('mobile-drawer-open');
    
    if (isVisible) {
      this.closeMobileDrawer();
    } else {
      this.openMobileDrawer();
    }
  }

  /**
   * Open mobile drawer
   */
  openMobileDrawer() {
    this.openMobileDrawerById('mobile-drawer-section');
  }

  /**
   * Open a specific mobile drawer by element id.
   * Defaults to the main course drawer (`mobile-drawer-section`) to preserve existing behavior.
   * @param {string} drawerId
   */
  openMobileDrawerById(drawerId = 'mobile-drawer-section') {
    const drawer = document.getElementById(drawerId);
    const backdrop = document.getElementById('mobile-drawer-backdrop');
    const mobileTrigger = document.getElementById('mobile-course-trigger');

    if (drawer && backdrop) {
      backdrop.classList.add('show');
      drawer.classList.add('mobile-drawer-open');

      if (drawerId === 'mobile-drawer-section' && window.shopifySettings?.studentIDActivation) {
        drawer.classList.add('studentId-Flow-Enabled');
      }

      drawer.style.transform = 'translateY(0)';
      document.body.style.overflow = 'hidden';

      if (drawerId === 'mobile-drawer-section') {
        this.initializeCourseSearch();
        this.checkMobileDrawerEmptyState();
      }
    }

    if (drawerId === 'mobile-drawer-section' && mobileTrigger) {
      mobileTrigger.style.display = 'none';
    }
  }
  
  // Check and handle empty state for mobile drawer
  checkMobileDrawerEmptyState() {
    window.courseMaterialsMobile?.ensureMobileTagsMatchPageSelection?.();

    const selectedCourses = document.querySelectorAll('.mobile-course-tag');
    const drawerContent = document.querySelector('.mobile-drawer-content');
    const hasMobileTags = selectedCourses && selectedCourses.length > 0;
    const hasPageResults =
      window.courseMaterialsMobile?.hasActiveCourseSelectionOrResults?.() ===
      true;

    if (drawerContent) {
      if (!hasMobileTags && !hasPageResults) {
        drawerContent.classList.add('empty-state');

        if (window.courseMaterialsMobile) {
          window.courseMaterialsMobile.showInitialPlaceholder();
        }
      } else {
        drawerContent.classList.remove('empty-state');

        if (window.courseMaterialsMobile) {
          window.courseMaterialsMobile.hideInitialPlaceholder();
        }
      }
    }
    
    // Update submit button state
    if (window.courseMaterialsMobile) {
      window.courseMaterialsMobile.updateMobileSubmitButtonState();
    }
  }

  /**
   * Initialize course search functionality
   */
  initializeCourseSearch() {
    // Initialize campus/term dropdowns in mobile drawer

    const campusSelect = cmrpCourseBuilderCampusTermEl('campus');
    if (!campusSelect?.value) {
      this.populateCampus();
    }

    const termSelect = cmrpCourseBuilderCampusTermEl('term');
    
    if (campusSelect?.value && termSelect?.value) {
      this.loadAllCourses();
    }
  }

  /**
   * Close mobile drawer
   */
  closeMobileDrawer() {
    const drawer = document.getElementById('mobile-drawer-section');
    const accessDrawer = document.getElementById('mobile-drawer-access-program');
    const backdrop = document.getElementById('mobile-drawer-backdrop');
    const mobileTrigger = document.getElementById('mobile-course-trigger');
    
    if (drawer && backdrop) {
      // Animate drawer out
      drawer.classList.remove('mobile-drawer-open');
      drawer.style.transform = 'translateY(100%)'; // Hide drawer
      backdrop.classList.remove('show');

      // Remove empty state class when closing
      const drawerContent = document.querySelector('.mobile-drawer-content');
      if (drawerContent) {
        drawerContent.classList.remove('empty-state');
      }
    }

    window.courseMaterialsMobile?.syncMobileDrawerBodyScroll?.();

    if (mobileTrigger) {
      mobileTrigger.style.display = 'block';
    }

    if (accessDrawer && backdrop) {
      accessDrawer.classList.remove('mobile-drawer-open');
      accessDrawer.style.transform = 'translateY(100%)';
    }
  }

  /**
   * Close Access Program mobile drawer
   */
  closeAccessProgramMobileDrawer() {
    const drawer = document.getElementById('mobile-drawer-access-program');
    const backdrop = document.getElementById('mobile-drawer-backdrop');

    if (drawer && backdrop) {
      drawer.classList.remove('mobile-drawer-open');
      drawer.style.transform = 'translateY(100%)';
      backdrop.classList.remove('show');
    }

    window.courseMaterialsMobile?.syncMobileDrawerBodyScroll?.();
  }

  /**
   * Initialize mobile drawer on page load - ensure drawer starts closed (no auto-open).
   */
  initializeMobileDrawerOnLoad() {
    if (cmrpIsCourseMaterialsResultsPage()) return;
    if (window.innerWidth <= 968) {
      const drawer = document.getElementById('mobile-drawer-section');
      const backdrop = document.getElementById('mobile-drawer-backdrop');
      
      if (drawer && backdrop) {
        // Ensure drawer starts closed (no mobile-drawer-open on load)
        drawer.classList.remove('mobile-drawer-open');
        drawer.style.transform = 'translateY(100%)';
        backdrop.classList.remove('show');
        
        this.initializeCourseSearch();
      }
    }
  }

  /**
   * Add course chip. sectionKey must match selectedFilters / URL (option value).
   * sectionLabel is optional display text (option label); defaults to sectionKey.
   */
  addSelectedCourse(department, course, sectionKey, sectionLabel) {
    const container = document.getElementById(
      cmrpId('selected-courses-container', 'cb'),
    );
    if (!container) return;

    const keySection = sectionKey != null && sectionKey !== '' ? String(sectionKey) : '';
    if (!keySection) return;

    const displaySection =
      sectionLabel != null && String(sectionLabel).trim() !== ''
        ? String(sectionLabel).trim()
        : keySection;

    const courseKey = `${department}-${course}-${keySection}`;

    // Check if course already exists
    const existingChip = container.querySelector(`[data-course-key="${CSS.escape(courseKey)}"]`);
    if (existingChip) return;

    const courseChip = document.createElement('div');
    courseChip.className = 'course-chip';
    courseChip.setAttribute('data-course-key', courseKey);
    courseChip.setAttribute('data-department', department);
    courseChip.setAttribute('data-course', course);
    courseChip.setAttribute('data-section', keySection);

    const chipText = document.createElement('span');
    chipText.className = 'course-chip-text';
    chipText.textContent = `${department} | ${course} | ${displaySection}`;

    const removeEl = document.createElement('div');
    removeEl.className = 'course-chip-remove';
    removeEl.setAttribute('role', 'button');
    removeEl.setAttribute('tabindex', '0');
    removeEl.setAttribute('aria-label', 'Remove course');
    removeEl.textContent = '×';
    const onRemove = e => {
      e.preventDefault();
      e.stopPropagation();
      const cmPage =
        window.courseMaterialsPage || window.courseMaterialsPageInstance;
      cmPage?.removeCourseSelection?.(department, course, keySection);
    };
    removeEl.addEventListener('click', onRemove);
    removeEl.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') onRemove(e);
    });

    courseChip.addEventListener('click', e => {
      if (e.target instanceof Element && e.target.closest('.course-chip-remove')) {
        return;
      }
      const cmPage =
        window.courseMaterialsPage || window.courseMaterialsPageInstance;
      cmPage?.removeCourseSelection?.(department, course, keySection);
    });

    courseChip.appendChild(chipText);
    courseChip.appendChild(removeEl);

    container.appendChild(courseChip);
  }

  /**
   * Remove desktop bubbles and mobile tags for one course (trimmed attribute match).
   * @param {string} department
   * @param {string} course
   * @param {string} section
   */
  clearCourseSelectionUI(department, course, section) {
    const t = v =>
      v == null || v === '' ? '' : String(v).trim();
    const looseEq = (a, b) => {
      const n = v =>
        String(v ?? '')
          .trim()
          .toLowerCase()
          .replace(/[\s_-]+/g, '');
      return n(a) === n(b);
    };
    const d = t(department);
    const c = t(course);
    const s = t(section);
    if (!d || !c || !s) return;

    const match = el => {
      const ed = el.getAttribute('data-department');
      const ec = el.getAttribute('data-course');
      const es = el.getAttribute('data-section');
      const exact =
        t(ed) === d && t(ec) === c && t(es) === s;
      const loose =
        looseEq(ed, d) && looseEq(ec, c) && looseEq(es, s);
      return exact || loose;
    };

    [
      document.getElementById(cmrpId('selected-courses-container', 'cb')),
      document.getElementById('mobile-selected-courses'),
    ].forEach(root => {
      if (!root) return;
      root
        .querySelectorAll('.course-chip, .mobile-course-tag')
        .forEach(chip => {
          if (match(chip)) chip.remove();
        });
    });

    const mobileWrap = document.getElementById('mobile-selected-courses');
    if (
      mobileWrap &&
      !mobileWrap.querySelector('.mobile-course-tag')
    ) {
      mobileWrap.style.display = 'none';
    }
  }

  // Get all selected courses
  getSelectedCourses() {
    const chips = document.querySelectorAll('.course-chip');
    return Array.from(chips).map(chip => ({
      division: chip.getAttribute('data-division'),
      department: chip.getAttribute('data-department'),
      course: chip.getAttribute('data-course'),
      section: chip.getAttribute('data-section'),
      key: chip.getAttribute('data-course-key')
    }));
  }

  /**
   * Sync course bubbles from filter data
   * Called when products are displayed to ensure bubbles match the rendered courses.
   * Rebuilds the desktop chip list each time (add-only sync left stale course-builder chips
   * after a student-ID lookup replaced selectedFilters). Only shows course-builder rows
   * (not fromStudentId), matching mobile #mobile-selected-courses behavior.
   * @param {Array} filters - Array of filter objects with selectedDepartment, selectedcourse, selectedsection
   */
  syncBubblesFromFilters(filters) {
    const container = document.getElementById(
      cmrpId('selected-courses-container', 'cb'),
    );
    if (!container) {
      if (filters && Array.isArray(filters) && filters.length > 0) {
        setTimeout(() => this.syncBubblesFromFilters(filters), 500);
      }
      return;
    }

    if (!filters || !Array.isArray(filters) || filters.length === 0) {
      container.innerHTML = '';
      if (
        window.courseMaterialsMobile &&
        typeof window.courseMaterialsMobile.syncMobileTagsFromFilters === 'function'
      ) {
        window.courseMaterialsMobile.syncMobileTagsFromFilters([]);
      }
      return;
    }

    container.innerHTML = '';

    filters.forEach(filter => {
      if (
        !filter ||
        filter.fromStudentId ||
        !filter.selectedDepartment ||
        !filter.selectedcourse ||
        !filter.selectedsection
      ) {
        return;
      }
      const department = filter.selectedDepartment;
      const course = filter.selectedcourse;
      const section = filter.selectedsection;
      this.addSelectedCourse(department, course, section);
    });

    if (
      window.courseMaterialsMobile &&
      typeof window.courseMaterialsMobile.syncMobileTagsFromFilters === 'function'
    ) {
      window.courseMaterialsMobile.syncMobileTagsFromFilters(filters);
    }
  }
}

/** CMRP desktop sidebar breakpoint (matches max-width: 968px mobile rules). */
const CMRP_DESKTOP_SIDEBAR_MQ = '(min-width: 969px)';

/**
 * Once student-id-mode is known, show the course-details heading in the DOM, then clear
 * data-cmrp-flow-pending so .filters-content body is shown (avoids a flash before layout state is applied).
 */
function applyCmrpCourseDetailsSectionFlowUi() {
  const filtersContent = document.querySelector('.filters-content');
  const headingCb = document.getElementById(
    cmrpId('cmrp-course-details-heading', 'cb'),
  );
  const headingSid = document.getElementById(
    cmrpId('cmrp-course-details-heading', 'sid'),
  );
  const filters = document.getElementById('filters-section');

  const desktop =
    typeof window.matchMedia !== 'undefined' &&
    window.matchMedia(CMRP_DESKTOP_SIDEBAR_MQ).matches;

  const cmrpResults = cmrpIsCourseMaterialsResultsPage();
  const wideCmrpUi = desktop || cmrpResults;

  const dual = filters != null && cmrpShowBothForms();
  let showCbHeading = false;
  let showSidHeading = false;
  if (dual && wideCmrpUi) {
    showCbHeading = true;
    const glassStudentForm = document.querySelector(
      '.cmrp-filter-form--student-id.cmrp-student-id-layout--glass',
    );
    showSidHeading = !glassStudentForm;
  } else if (filters != null && wideCmrpUi) {
    showCbHeading = !filters.classList.contains('student-id-mode');
  }

  if (headingCb) {
    if (showCbHeading) {
      headingCb.removeAttribute('hidden');
    } else {
      headingCb.setAttribute('hidden', '');
    }
  }
  if (headingSid) {
    if (showSidHeading) {
      headingSid.removeAttribute('hidden');
    } else {
      headingSid.setAttribute('hidden', '');
    }
  }

  filtersContent?.removeAttribute('data-cmrp-flow-pending');
}

window.applyCmrpCourseDetailsSectionFlowUi = applyCmrpCourseDetailsSectionFlowUi;

if (typeof window !== 'undefined' && typeof window.matchMedia !== 'undefined') {
  window.matchMedia(CMRP_DESKTOP_SIDEBAR_MQ).addEventListener('change', () => {
    applyCmrpCourseDetailsSectionFlowUi();
  });
}

function consumeCmrpStudentIdPrefill() {
  try {
    const raw = sessionStorage.getItem(CMRP_STUDENT_ID_PREFILL_KEY);
    if (!raw) return '';
    sessionStorage.removeItem(CMRP_STUDENT_ID_PREFILL_KEY);
    return String(raw).trim();
  } catch (e) {
    return '';
  }
}

function applyCmrpStudentIdPrefillToInputs() {
  const v = consumeCmrpStudentIdPrefill();
  if (!v) return;
  const d = document.getElementById(cmrpId('student-id','sid'));
  const m = document.getElementById('mobile-student-id-input');
  if (d) d.value = v;
  if (m) m.value = v;
}

window.CMRP_STUDENT_ID_PREFILL_KEY = CMRP_STUDENT_ID_PREFILL_KEY;
window.consumeCmrpStudentIdPrefill = consumeCmrpStudentIdPrefill;
window.applyCmrpStudentIdPrefillToInputs = applyCmrpStudentIdPrefillToInputs;

async function cmrpGlobalCheckSession() {
  const token = sessionStorage.getItem('sessionToken');
  if (!token) return false;
  try {
    const response = await fetch('/apps/shopifyData/session/check', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error(`Error: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error checking session:', error);
  }
}

function reorderStudentIdFlowDesktopLayout() {
  if (
    !window.matchMedia ||
    (!window.matchMedia('(min-width: 969px)').matches &&
      !cmrpIsCourseMaterialsResultsPage())
  ) {
    return;
  }
  const sidRoot = document.querySelector('.cmrp-filter-form--student-id');
  if (!sidRoot) return;
  const glassLayout = sidRoot.classList.contains('cmrp-student-id-layout--glass');
  const courseDetailsSection = sidRoot.querySelector('.course-details-section');
  const student_id_section = sidRoot.querySelector('.student-id-section');
  const desktopSubmitContainer = sidRoot.querySelector('.student-id-submit-container-desktop');
  const submitStudentIdButton = document.getElementById(
    cmrpId('submit-student-id', 'sid'),
  );
  if (!glassLayout && courseDetailsSection && student_id_section) {
    sidRoot.insertBefore(student_id_section, courseDetailsSection);
  }
  if (desktopSubmitContainer && submitStudentIdButton) {
    if (!desktopSubmitContainer.contains(submitStudentIdButton)) {
      desktopSubmitContainer.appendChild(submitStudentIdButton);
    }
    desktopSubmitContainer.style.display = 'flex';
  }
  anchorCmrpProductsPanel();
}

/**
 * Dual CMRP: student-id form is rendered first; skip scroll when the target is already that top form.
 * (Avoids no-op scroll; single-form pages always return false here.)
 * @param {Element | null} el
 */
function cmrpSkipScrollToFormBecauseFirst(el) {
  if (!el || !cmrpShowBothForms()) return false;
  const content = document
    .getElementById('filters-section')
    ?.querySelector('.filters-content');
  if (!content) return false;
  const forms = content.querySelectorAll('.cmrp-filter-form');
  if (forms.length < 2) return false;
  return forms[0] === el;
}

function scrollCmrpToCourseBuilder() {
  const el = document.querySelector('.cmrp-filter-form--course-builder');
  if (!el) return;
  if (cmrpSkipScrollToFormBecauseFirst(el)) return;
  try {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } catch (e) {
    el.scrollIntoView(true);
  }
}
window.scrollCmrpToCourseBuilder = scrollCmrpToCourseBuilder;

function cmrpHasDeepCourseUrlParams() {
  const p = new URLSearchParams(window.location.search);
  return Boolean(
    p.get('departmentDisplayName') ||
      p.get('courseDisplayName') ||
      p.get('sectionDisplayName'),
  );
}

function cmrpScrollToFormViewportOk() {
  return (
    cmrpIsCourseMaterialsResultsPage() ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width: 969px)').matches)
  );
}

function scrollCmrpToStudentIdForm() {
  const el = document.querySelector('.cmrp-filter-form--student-id');
  if (!el) return;
  if (cmrpSkipScrollToFormBecauseFirst(el)) return;
  try {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } catch (e) {
    el.scrollIntoView(true);
  }
}
window.scrollCmrpToStudentIdForm = scrollCmrpToStudentIdForm;

/**
 * Landed from React / header / `?cmrp_flow=` (see `cmrpMarkPendingFormEntryScroll`), no course-line URL params.
 */
function scrollCmrpToFormForFlow(flow) {
  if (!cmrpScrollToFormViewportOk()) return;
  if (!cmrpShowBothForms()) return;
  if (cmrpHasDeepCourseUrlParams()) return;
  const f = String(flow || '').trim().toLowerCase();
  if (f === 'course-builder') {
    scrollCmrpToCourseBuilder();
  } else if (f === 'student-id' || f === 'sso') {
    scrollCmrpToStudentIdForm();
  }
}

function queueCmrpFlowEntryScrollFromSession() {
  if (!cmrpShowBothForms()) return;
  if (cmrpHasDeepCourseUrlParams()) return;
  if (!cmrpConsumePendingFormEntryScroll()) return;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => scrollCmrpToFormForFlow(cmrpCurrentFlow()));
  });
}

/**
 * After URL restore produced filters: if `shopBy` is set, scroll to the matching dual-form column.
 */
function scrollCmrpToFormForShopByParam() {
  if (!cmrpScrollToFormViewportOk()) return;
  if (!cmrpShowBothForms()) return;
  let shopBy = '';
  try {
    const raw = new URLSearchParams(window.location.search).get('shopBy');
    shopBy = raw ? decodeURIComponent(raw) : '';
  } catch {
    shopBy = new URLSearchParams(window.location.search).get('shopBy') || '';
  }
  const normalized = String(shopBy).trim().toLowerCase();
  if (!normalized) return;
  if (normalized === 'course' || normalized === 'courserefid') {
    scrollCmrpToCourseBuilder();
  } else if (normalized === 'studentid') {
    scrollCmrpToStudentIdForm();
  }
}
window.scrollCmrpToFormForShopByParam = scrollCmrpToFormForShopByParam;

/**
 * After URL restore + product refresh on CMRP results: scroll to the docked results panel
 * (`#products-section`), not the filter form.
 */
function scrollCmrpToResultsAfterUrlRestore() {
  if (!cmrpScrollToFormViewportOk()) return;
  const el = document.getElementById('products-section');
  if (!el) return;
  const offsetAbovePx = 150;
  const rect = el.getBoundingClientRect();
  const top =
    window.scrollY + rect.top - offsetAbovePx;
  const clamped = Math.max(0, top);
  try {
    window.scrollTo({ top: clamped, left: 0, behavior: 'smooth' });
  } catch (e) {
    window.scrollTo(0, clamped);
  }
}
window.scrollCmrpToResultsAfterUrlRestore = scrollCmrpToResultsAfterUrlRestore;

async function refreshStudentIdSessionUI() {
  const searchParams = new URLSearchParams(window.location.search);
  const hashValue = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const hashParams = new URLSearchParams(hashValue);
  const hasCodeParam =
    Boolean(searchParams.get('code')) || Boolean(hashParams.get('code'));

  const courseLineFields = cmrpCourseBuilderCourseLineFieldEls();
  const dualForms = cmrpShowBothForms();
  if (courseLineFields.length && !dualForms) {
    courseLineFields.forEach(el => {
      el.style.display = 'none';
    });
  }
  const session_check = await cmrpGlobalCheckSession();

  if (session_check && session_check.status) {
    const filtersSectionEarly = document.getElementById('filters-section');
    if (filtersSectionEarly) filtersSectionEarly.classList.add('student-id-mode');
    const studentIdInfoEarly = document.getElementById(
      cmrpId('student-id-info-desktop', 'sid'),
    );
    if (
      studentIdInfoEarly &&
      window.matchMedia &&
      (window.matchMedia('(min-width: 768px)').matches ||
        cmrpIsCourseMaterialsResultsPage())
    ) {
      studentIdInfoEarly.style.display = 'flex';
    }
    reorderStudentIdFlowDesktopLayout();
    const student_id_section = document.querySelector(
      '.cmrp-filter-form--student-id .student-id-section',
    );
    applyCmrpTypedStudentIdSectionVisibility(student_id_section, { dualForms });
    const studentIdInput = document.getElementById(cmrpId('student-id', 'sid'));
    if (studentIdInput) {
      studentIdInput.value = session_check.sessionID;
    }
    if (courseLineFields.length && !dualForms) {
      courseLineFields.forEach(el => {
        el.style.display = 'none';
      });
    }
  } else {
    sessionStorage.removeItem('sessionToken');
    const student_id_section = document.querySelector(
      '.cmrp-filter-form--student-id .student-id-section',
    );
    applyCmrpTypedStudentIdSectionVisibility(student_id_section, { dualForms });
    if (courseLineFields.length) {
      courseLineFields.forEach(el => {
        el.style.display = dualForms ? '' : hasCodeParam ? 'none' : '';
      });
    }
  }
  anchorCmrpProductsPanel();
  window.syncCmrpSsoLandingPanelFromSession?.();
  syncCmrpTypedStudentIdDomFlags();
}

window.refreshStudentIdSessionUI = refreshStudentIdSessionUI;
window.syncCmrpTypedStudentIdDomFlags = syncCmrpTypedStudentIdDomFlags;
window.syncCmrpSsoLandingPanelFromSession = function syncCmrpSsoLandingPanelFromSession() {
  const sb = window.courseMaterialsSidebar;
  if (sb && typeof sb.syncCmrpSsoLandingPanelFromSession === 'function') {
    sb.syncCmrpSsoLandingPanelFromSession();
  } else {
    syncCmrpTypedStudentIdDomFlags();
  }
};
window.reorderStudentIdFlowDesktopLayout = reorderStudentIdFlowDesktopLayout;

document.addEventListener('DOMContentLoaded', async () => {
  syncCmrpTypedStudentIdDomFlags();
  cmrpHydrateFlowFromQueryAndCleanUrl();
  cmrpAttachCmrpSelectTruncationListeners();
  cmrpInstallCmrpSelectTruncationMutationObserver();
  requestAnimationFrame(() => {
    window.cmrpTruncateCmrpFilterSelectsIn?.(
      document.getElementById('filters-section'),
    );
  });

  document.addEventListener(
    'product-display:updated',
    () => {
      requestAnimationFrame(() =>
        window.cmrpTruncateCmrpFilterSelectsIn?.(
          document.getElementById('filters-section'),
        ),
      );
    },
    { passive: true },
  );

  const themeStudentId =
    document.getElementById('filters-section')?.dataset?.studentIdActivation ===
    'true';
  if (themeStudentId) {
    (function ensureDefaultCmrpFlowFromTheme() {
      if (cmrpCurrentFlow()) return;
      const p = new URLSearchParams(window.location.search);
      if (
        p.get('sectionDisplayName') ||
        p.get('courseDisplayName') ||
        p.get('departmentDisplayName')
      ) {
        return;
      }
      cmrpPersistFlow('student-id');
      if (typeof window.setCmrpInitialFlowPreference === 'function') {
        window.setCmrpInitialFlowPreference('student-id');
      }
    })();
  }

  const forcedFlow = cmrpCurrentFlow();
  const filtersSection = document.getElementById('filters-section');

  if (forcedFlow === 'student-id' || forcedFlow === 'sso') {
    const student_id_section = document.querySelector(
      '.cmrp-filter-form--student-id .student-id-section',
    );
    applyCmrpTypedStudentIdSectionVisibility(student_id_section, {
      dualForms: cmrpShowBothForms(),
    });
    const studentIdInfoDesktop = document.getElementById(
      cmrpId('student-id-info-desktop', 'sid'),
    );
    if (
      studentIdInfoDesktop &&
      window.matchMedia &&
      (window.matchMedia('(min-width: 969px)').matches ||
        cmrpIsCourseMaterialsResultsPage())
    ) {
      studentIdInfoDesktop.style.display = 'flex';
    }
    if (filtersSection) filtersSection.classList.add('student-id-mode');

    if (
      window.matchMedia &&
      (window.matchMedia('(min-width: 969px)').matches ||
        cmrpIsCourseMaterialsResultsPage())
    ) {
      reorderStudentIdFlowDesktopLayout();
    }
    window.applyCmrpCourseDetailsSectionFlowUi?.();
    const session_check = await cmrpGlobalCheckSession();
    if (session_check && session_check.status && session_check.sessionID) {
      const studentIdInput = document.getElementById(cmrpId('student-id', 'sid'));
      if (studentIdInput) {
        studentIdInput.value = session_check.sessionID;
      }
      try {
        sessionStorage.removeItem('cmrp_student_id_prefill');
      } catch (e) {}
    } else if (typeof applyCmrpStudentIdPrefillToInputs === 'function') {
      applyCmrpStudentIdPrefillToInputs();
    } else {
      (function applyCmrpStudentIdPrefillFallback() {
        try {
          const k = 'cmrp_student_id_prefill';
          const v = sessionStorage.getItem(k);
          if (!v) return;
          sessionStorage.removeItem(k);
          const t = String(v).trim();
          if (!t) return;
          const d = document.getElementById(cmrpId('student-id', 'sid'));
          const m = document.getElementById('mobile-student-id-input');
          if (d) d.value = t;
          if (m) m.value = t;
        } catch (e) {}
      })();
    }
    scheduleCmrpAnchorProductsPanel();
    queueCmrpFlowEntryScrollFromSession();
    window.syncCmrpSsoLandingPanelFromSession?.();
    return;
  }

  if (forcedFlow === 'course-builder') {
    sessionStorage.removeItem('sessionToken');
    if (filtersSection) filtersSection.classList.remove('student-id-mode');
    window.applyCmrpCourseDetailsSectionFlowUi?.();
    scheduleCmrpAnchorProductsPanel();
    queueCmrpFlowEntryScrollFromSession();
    window.syncCmrpSsoLandingPanelFromSession?.();
    return;
  }

  await refreshStudentIdSessionUI();
  const session_check = await cmrpGlobalCheckSession();
  if (filtersSection) {
    if (session_check && session_check.status) {
      cmrpPersistFlow('student-id');
      filtersSection.classList.add('student-id-mode');
      const studentIdInfoDesktop = document.getElementById(
        cmrpId('student-id-info-desktop', 'sid'),
      );
      if (
        studentIdInfoDesktop &&
        window.matchMedia &&
        (window.matchMedia('(min-width: 969px)').matches ||
          cmrpIsCourseMaterialsResultsPage())
      ) {
        studentIdInfoDesktop.style.display = 'flex';
      }
      const student_id_section = document.querySelector(
        '.cmrp-filter-form--student-id .student-id-section',
      );
      applyCmrpTypedStudentIdSectionVisibility(student_id_section, {
        dualForms: cmrpShowBothForms(),
      });
      if (
        student_id_section &&
        window.matchMedia &&
        (window.matchMedia('(min-width: 969px)').matches ||
          cmrpIsCourseMaterialsResultsPage())
      ) {
        reorderStudentIdFlowDesktopLayout();
      }
    } else {
      cmrpPersistFlow('course-builder');
      filtersSection.classList.remove('student-id-mode');
    }
  }
  window.applyCmrpCourseDetailsSectionFlowUi?.();
  scheduleCmrpAnchorProductsPanel();
  queueCmrpFlowEntryScrollFromSession();
  window.syncCmrpSsoLandingPanelFromSession?.();
});

let cmrpAnchorProductsResizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(cmrpAnchorProductsResizeTimer);
  cmrpAnchorProductsResizeTimer = setTimeout(() => anchorCmrpProductsPanel(), 120);
});

document.addEventListener('sidebar:courseAdded', () => {
  requestAnimationFrame(() => anchorCmrpProductsPanel());
});
document.addEventListener('sidebar:studentSubmitted', () => {
  requestAnimationFrame(() => anchorCmrpProductsPanel());
});

// Export for global use
window.CourseMaterialsSidebar = CourseMaterialsSidebar;
